// src/screens/SmartLockPage.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Center,
  Flex,
  Button,
  TextInput,
  Text,
  Title,
  Box,
  Image,
} from "@mantine/core";
import {
  FaTools,
  FaDollarSign,
  FaRegClock,
  FaUser,
  FaChevronLeft,
  FaQrcode,
  FaMapMarkerAlt,
  FaTicketAlt,
  FaIdCard,
  FaArrowRight,
} from "react-icons/fa";
import Confetti from "react-confetti";
import Lottie from "lottie-react";
import PhoneInput from "react-phone-number-input";
import { GooglePlayButton, AppStoreButton } from "react-mobile-app-button";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { motion } from "framer-motion";

// Lottie JSONs
import wclogoAnimation from "../assets/lottie/wclogo.json";
import signalAnimation from "../assets/lottie/signal.json";
import unlockingAnimation from "../assets/lottie/unlocking.json";
import unlockedAnimation from "../assets/lottie/unlocked.json";
import errorAnimation from "../assets/lottie/error.json";
import imagePlaceholder from "../assets/lottie/imgplaceholder.json";

// Static images
import czbackground from "../assets/images/czbackground.png";

import { CONFIG } from "../atoms/config";
import {
  checkUserAuth as legacyCheckUserAuth,
  confirmVerificationCode as legacyConfirmVerificationCode,
  unlockAgain as legacyUnlockAgain,
  activateHiredSession,
  deactivateHiredSession,
} from "../atoms/network";
import { DotNetAPI } from "../atoms/dotnetAPI";
import moment from "moment-timezone";

import StartHireView from "./StartHireView";
import LockSessionHireView from "./LockHireSessionView";
import ReportDamageFlow from "../atoms/ReportDamageFlow";

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────
const S = {
  fullHeightContainer: { minHeight: "100vh" },
  fullScreenContainer: {
    position: "relative",
    minHeight: "100vh",
    overflow: "hidden",
  },
  fullHeightCenter: {
    height: "100%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  logoCircleWrapper: (size = 175) => ({
    position: "relative",
    width: size,
    height: size,
    marginTop: 110,
    zIndex: 1,
  }),
  logoCircleBg: (size = 175) => ({
    position: "absolute",
    top: 0,
    left: 0,
    width: size,
    height: size,
    borderRadius: "50%",
    backgroundColor: "white",
    zIndex: 0,
  }),
  labelLeftAlign: { style: { textAlign: "left" } },
  paperMax: { maxWidth: 480, marginInline: "auto", textAlign: "left" },
  unlockedBox: {
    textAlign: "center",
    zIndex: 1,
    alignItems: "center",
    width: "100%",
    maxWidth: 520,
  },
  lottieUnlocked: { width: 132, height: 132 },
  lottieVerify: { width: 200, height: 230, marginTop: 16 },
  lottieError: { width: 200, height: 200 },
  lottieSignal: { opacity: 0.5, width: 500, height: 500, maxWidth: "120vw" },
  lottieUnlocking: { width: 125, height: 125 },
  bgImage: { position: "absolute", top: 0, left: 0, zIndex: 0 },
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function truthy(v) {
  return v === true || v === 1 || v === "1" || v === "true";
}

function toNumber(value, fallback = 0) {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function canPurchaseHireSession(item) {
  return (
    toNumber(item?.hire_cost_base, 0) > 0 ||
    toNumber(item?.hire_cost_base_person, 0) > 0
  );
}

function flattenSessionOpeningSlots(source, includeDateKey = false) {
  if (!source) {
    return [];
  }

  if (Array.isArray(source)) {
    return source
      .flat(Infinity)
      .filter(
        (slot) => slot && typeof slot === "object" && !Array.isArray(slot),
      );
  }

  if (typeof source === "object") {
    const result = [];

    Object.entries(source).forEach(([dateKey, value]) => {
      const rows = Array.isArray(value) ? value.flat(Infinity) : [value];

      rows.forEach((slot) => {
        if (!slot || typeof slot !== "object" || Array.isArray(slot)) {
          return;
        }

        result.push(
          includeDateKey
            ? {
              ...slot,
              __dateKey: slot.specific_date ? undefined : dateKey,
            }
            : slot,
        );
      });
    });

    return result;
  }

  return [];
}

function sessionTimeToSeconds(value, { isEnd = false } = {}) {
  if (!value) {
    return null;
  }

  const [hoursRaw, minutesRaw, secondsRaw = "0"] = String(value)
    .slice(0, 8)
    .split(":");

  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const seconds = Number(secondsRaw);

  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    !Number.isInteger(seconds) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59 ||
    seconds < 0 ||
    seconds > 59
  ) {
    return null;
  }

  // 23:59 is the explicit end-of-day option in the admin UI.
  if (isEnd && hours === 23 && minutes === 59) {
    return 24 * 60 * 60;
  }

  return hours * 60 * 60 + minutes * 60 + seconds;
}

function getSessionSpecificDateKey(slot, timezone) {
  if (slot?.__dateKey && /^\d{4}-\d{2}-\d{2}$/.test(String(slot.__dateKey))) {
    return String(slot.__dateKey);
  }

  let rawDate = slot?.specific_date;

  if (rawDate && typeof rawDate === "object" && rawDate.date) {
    rawDate = rawDate.date;
  }

  if (!rawDate) {
    return null;
  }

  const parsed = moment.tz(rawDate, timezone);

  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
}

function formatSessionOpeningTime(value) {
  return value ? String(value).slice(0, 5) : "";
}

function getSessionPurchaseAvailability({
  weekdays,
  specificDates,
  siteTimezone,
}) {
  const weeklySlots = flattenSessionOpeningSlots(weekdays);
  const specificSlots = flattenSessionOpeningSlots(specificDates, true);

  const timezoneSlot = [...weeklySlots, ...specificSlots].find(
    (slot) => typeof slot?.timezone === "string" && slot.timezone.trim(),
  );

  const timezone = siteTimezone || timezoneSlot?.timezone || moment.tz.guess();

  const now = moment.tz(timezone);

  const hasSchedule = weeklySlots.length > 0 || specificSlots.length > 0;

  const slotsForDate = (dateMoment) => {
    const dateKey = dateMoment.format("YYYY-MM-DD");

    const specificForDate = specificSlots.filter(
      (slot) => getSessionSpecificDateKey(slot, timezone) === dateKey,
    );

    const source =
      specificForDate.length > 0
        ? specificForDate
        : weeklySlots.filter(
          (slot) =>
            Number(slot?.week_day_index) === dateMoment.isoWeekday() - 1,
        );

    return source
      .filter((slot) => !truthy(slot?.is_closed))
      .map((slot) => ({
        ...slot,
        startSeconds: sessionTimeToSeconds(slot?.time_open),
        endSeconds: sessionTimeToSeconds(slot?.time_close, {
          isEnd: true,
        }),
      }))
      .filter(
        (slot) =>
          slot.startSeconds !== null &&
          slot.endSeconds !== null &&
          slot.startSeconds < slot.endSeconds,
      )
      .sort((a, b) => a.startSeconds - b.startSeconds);
  };

  // No hours configured means no frontend restriction.
  if (!hasSchedule) {
    return {
      hasSchedule: false,
      isOpen: true,
      timezone,
      currentTimeLabel: now.format("HH:mm"),
      todayHoursLabel: "No restricted hours configured",
      nextOpeningLabel: null,
      currentSlot: null,
      currentSlotCloseTime: null,
      currentSlotRemainingMinutes: null,
    };
  }

  const currentSeconds =
    now.hours() * 60 * 60 + now.minutes() * 60 + now.seconds();

  const todaySlots = slotsForDate(now);

  const currentSlot =
    todaySlots.find(
      (slot) =>
        currentSeconds >= slot.startSeconds && currentSeconds < slot.endSeconds,
    ) || null;

  const isOpen = Boolean(currentSlot);

  // end_til_close_time must use the CURRENT active slot only.
  // Example: 09:00-12:00 and 14:00-17:00 at 10:00 => close at 12:00.
  //
  // Calculate duration from the start of the current minute.
  // Example: 12:47:22 is treated as 12:47:00 for session length.
  const currentMinuteSeconds = now.hours() * 60 * 60 + now.minutes() * 60;

  const currentSlotRemainingMinutes = currentSlot
    ? Math.max(
      0,
      Math.floor((currentSlot.endSeconds - currentMinuteSeconds) / 60),
    )
    : null;

  const currentSlotCloseTime = currentSlot
    ? formatSessionOpeningTime(currentSlot.time_close)
    : null;

  const todayHoursLabel =
    todaySlots.length > 0
      ? todaySlots
        .map(
          (slot) =>
            `${formatSessionOpeningTime(
              slot.time_open,
            )} – ${formatSessionOpeningTime(slot.time_close)}`,
        )
        .join(", ")
      : "Closed today";

  let nextOpeningLabel = null;

  for (
    let dayOffset = 0;
    dayOffset <= 14 && !nextOpeningLabel;
    dayOffset += 1
  ) {
    const date = now.clone().add(dayOffset, "days").startOf("day");

    const slots = slotsForDate(date);

    for (const slot of slots) {
      if (dayOffset === 0 && slot.startSeconds <= currentSeconds) {
        continue;
      }

      const timeLabel = formatSessionOpeningTime(slot.time_open);

      if (dayOffset === 0) {
        nextOpeningLabel = `Today at ${timeLabel}`;
      } else if (dayOffset === 1) {
        nextOpeningLabel = `Tomorrow at ${timeLabel}`;
      } else {
        nextOpeningLabel = `${date.format("dddd D MMM")} at ${timeLabel}`;
      }

      break;
    }
  }

  return {
    hasSchedule: true,
    isOpen,
    timezone,
    currentTimeLabel: now.format("HH:mm"),
    todayHoursLabel,
    nextOpeningLabel,
    currentSlot,
    currentSlotCloseTime,
    currentSlotRemainingMinutes,
  };
}

function formatRemainingTime(totalSeconds) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function formatPhoneNumber(raw) {
  if (!raw) return "";

  if (String(raw).startsWith("+")) {
    return String(raw).replace(/\s+/g, "");
  }

  let numberValue = String(raw).replace(/\D+/g, "");

  if (numberValue.startsWith("0")) {
    numberValue = `+61${numberValue.slice(1)}`;
  }

  if (!numberValue.startsWith("+")) {
    numberValue = `+${numberValue}`;
  }

  return numberValue;
}

function apiStatusIsSuccess(json) {
  if (json?.status === true) return true;
  if (json?.status === 1) return true;
  if (json?.status === "1") return true;
  if (String(json?.status).toLowerCase() === "true") return true;

  return false;
}

function getApiMessage(json, fallback = "Something went wrong.") {
  return json?.message || json?.error || fallback;
}

function isOutsideOpeningHoursError(error) {
  const responseData = error?.response?.data || error?.apiData || {};

  const code = responseData?.code || error?.code || "";

  const message = responseData?.message || error?.message || "";

  return (
    code === "outside_opening_hours" ||
    String(message)
      .toLowerCase()
      .includes("cannot be unlocked outside the venue opening hours") ||
    String(message)
      .toLowerCase()
      .includes("cannot be unlocked outside the opening hours")
  );
}

function getMembershipField(membership, ...keys) {
  for (const key of keys) {
    const value = membership?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
}

function getMembershipId(membership) {
  return toNumber(
    getMembershipField(membership, "id", "Id", "membership_id", "membershipId"),
    0,
  );
}

function getMembershipPriceDollars(membership) {
  return toNumber(
    getMembershipField(
      membership,
      "membership_cost_base",
      "membershipCostBase",
      "MembershipCostBase",
    ),
    0,
  );
}

function formatMembershipPrice(membership) {
  const value = getMembershipPriceDollars(membership);

  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(value);
}

function customerHasActiveMembership(user) {
  const membershipId = toNumber(
    user?.membership_id ?? user?.membershipId ?? user?.MembershipId,
    0,
  );

  if (membershipId <= 0) {
    return false;
  }

  const rawEndDate =
    user?.membership_end_date ??
    user?.membershipEndDate ??
    user?.MembershipEndDate ??
    null;

  if (!rawEndDate) {
    return true;
  }

  const endTime = Date.parse(rawEndDate);

  if (!Number.isFinite(endTime)) {
    return true;
  }

  return endTime > Date.now();
}

function detectBrowserInfo() {
  if (typeof navigator === "undefined") {
    return { browser: "generic", os: "desktop" };
  }

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Mac/i.test(platform) && maxTouchPoints > 1);

  const isAndroid = /Android/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua) && !isIOS;
  const isWindows = /Windows/i.test(ua);

  let browser = "generic";

  if (/CriOS|Mozilla/i.test(ua)) {
    browser = "chrome";
  } else if (/EdgiOS|EdgA|Edg/i.test(ua)) {
    browser = "edge";
  } else if (/FxiOS|Firefox/i.test(ua)) {
    browser = "firefox";
  } else if (/OPiOS|OPR|Opera/i.test(ua)) {
    browser = "opera";
  } else if (
    /Safari/i.test(ua) &&
    !/CriOS|Chrome|EdgiOS|Edg|FxiOS|Firefox|OPiOS|OPR|Opera/i.test(ua)
  ) {
    browser = "safari";
  }

  console.log(ua, "yoyoyoyoyowwww");

  let os = "desktop";

  if (isIOS) os = "ios";
  else if (isAndroid) os = "android";
  else if (isMac) os = "mac";
  else if (isWindows) os = "windows";

  return { browser, os };
}

function getLocationHelpContent({ browserInfo, permissionState, errorCode }) {
  const denied = permissionState === "denied" || errorCode === 1;
  const { browser, os } = browserInfo || { browser: "generic", os: "desktop" };

  if (browser === "chrome" && os === "ios") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Chrome. We cannot show the browser prompt again until you change the permission settings for Chrome and this website."
        : "We could not confirm your location yet. Please allow location access in Chrome and try again.",
      steps: [
        "Open Settings on your iPhone",
        "Go to Privacy & Security > Location Services and make sure Location Services is on",
        "Scroll down to Chrome, tap Location, then choose Ask Next Time or While Using the App",
        "Open Chrome, tap More, then Settings > Content Settings",
        "Check the location permission for sites, then return here and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "safari" && os === "ios") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Safari. We cannot show the browser prompt again until you change the setting for Safari and this website."
        : "We could not confirm your location yet. Please allow location access in Safari and try again.",
      steps: [
        "Open Settings on your iPhone",
        "Go to Privacy & Security > Location Services and make sure Location Services is on",
        "Scroll to Safari Websites and choose Ask Next Time or While Using the App",
        "Open Safari and return to this page",
        "Tap the Page Menu button on the left side of the address bar, open Website Settings, and change Location to Ask or Allow",
        "Come back here and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "chrome" && os === "android") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Chrome. We cannot show the browser prompt again until you change the permission for this site."
        : "We could not confirm your location yet. Please allow location access in Chrome and try again.",
      steps: [
        "Make sure Location is turned on for your phone",
        "Open Chrome and go to this site",
        "Tap the site info icon to the left of the address bar, then tap Permissions",
        "Change Location to Allow",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (
    browser === "chrome" &&
    (os === "mac" || os === "windows" || os === "desktop")
  ) {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Chrome. We cannot show the browser prompt again until you change the site permission."
        : "We could not confirm your location yet. Please allow location access in Chrome and try again.",
      steps: [
        "Open Chrome Settings",
        "Go to Privacy and security > Site settings > Location",
        "Allow location access, or remove the blocked rule for this site",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "edge") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Edge. We cannot show the browser prompt again until you change the site permission."
        : "We could not confirm your location yet. Please allow location access in Edge and try again.",
      steps: [
        "Make sure Location Services are turned on for your device",
        "Open Edge site permissions for this page",
        "Change Location to Ask or Allow",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  if (browser === "firefox") {
    return {
      title: denied ? "Location Access Denied" : "Location Not Confirmed",
      message: denied
        ? "Location access was denied in Firefox. We cannot show the browser prompt again until you change the site permission."
        : "We could not confirm your location yet. Please allow location access in Firefox and try again.",
      steps: [
        "Make sure Location Services are turned on for your device",
        "Open Firefox site permissions for this page",
        "Change Location to Ask or Allow",
        "Return to this page and tap Try again",
      ],
      buttonLabel: "How to enable",
    };
  }

  return {
    title: denied ? "Location Access Denied" : "Location Not Confirmed",
    message: denied
      ? "Location access was denied for this browser. We cannot show the browser prompt again until you change the site permission."
      : "We could not get your location. Please check your device and browser location settings, then try again.",
    steps: [
      "Make sure Location Services are turned on for your device",
      "Open this browser’s site permissions for the current page",
      "Change Location to Ask or Allow",
      "Return to this page and tap Try again",
    ],
    buttonLabel: "How to enable",
  };
}

// Geolocation helpers
function getPositionOnce(opts = {}) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

function raceOnceWithWatch(opts = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let watchId = null;

    const clear = () => {
      if (watchId != null) {
        navigator.geolocation.clearWatch(watchId);
      }
    };

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          if (!settled) {
            settled = true;
            clear();
            resolve(pos);
          }
        },
        () => { },
        opts,
      );
    } catch {
      // ignore
    }

    getPositionOnce(opts)
      .then((pos) => {
        if (!settled) {
          settled = true;
          clear();
          resolve(pos);
        }
      })
      .catch((err) => {
        if (!settled) {
          settled = true;
          clear();
          reject(err);
        }
      });
  });
}

async function getBestEffortPosition({
  attempts = 3,
  baseDelayMs = 900,
  preciseOpts = {
    enableHighAccuracy: true,
    timeout: 12000,
    maximumAge: 15000,
  },
  coarseOpts = {
    enableHighAccuracy: false,
    timeout: 12000,
    maximumAge: 60000,
  },
} = {}) {
  let lastErr = null;

  for (let i = 0; i < attempts; i++) {
    try {
      const pos = await raceOnceWithWatch(preciseOpts);

      return {
        pos,
        coarse: false,
      };
    } catch (err) {
      lastErr = err;

      const isTransient =
        err &&
        (err.code === 2 || err.message?.includes("kCLErrorLocationUnknown"));

      if (!isTransient) {
        break;
      }

      const wait = baseDelayMs * Math.pow(1.5, i);

      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, wait));
    }
  }

  try {
    const pos = await raceOnceWithWatch(coarseOpts);

    return {
      pos,
      coarse: true,
    };
  } catch (err) {
    try {
      const pos = await getPositionOnce({
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: Infinity,
      });

      return {
        pos,
        coarse: true,
      };
    } catch (err2) {
      throw lastErr || err || err2;
    }
  }
}

function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const toRad = (x) => (x * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);

  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

function isWithinAllowedRadius({ user, target, accuracy, coarse }) {
  const baseRadius = 250;
  const coarseRadius = 1500;

  const dist = getDistanceFromLatLonInM(
    user.lat,
    user.lng,
    target.lat,
    target.lng,
  );

  const useRelaxed = coarse || (typeof accuracy === "number" && accuracy > 800);

  const allowed = useRelaxed ? coarseRadius : baseRadius;

  return {
    ok: dist <= allowed,
    dist,
    allowed,
    relaxed: useRelaxed,
  };
}

// ─────────────────────────────────────────────────────────────
// Auth flow states
// ─────────────────────────────────────────────────────────────
const AuthFlow = {
  UNKNOWN: "UNKNOWN",
  NO_LOCK: "NO_LOCK",
  NO_LOCATION: "NO_LOCATION",
  INPUT_DETAILS: "INPUT_DETAILS",
  LOADING: "LOADING",
  UNLOCKING: "UNLOCKING",
  ENTER_OTP: "ENTER_OTP",
  UNLOCKED: "UNLOCKED",
  EXPIRED: "EXPIRED",
  OUTSIDE_OPENING_HOURS: "OUTSIDE_OPENING_HOURS",
  NOT_NEAR_LOCATION: "NOT_NEAR_LOCATION",
  HIRE_SELECT: "HIRE_SELECT",
  LOCK_SESSION_HIRE: "LOCK_SESSION_HIRE",
  ACCESS_PURCHASE_CHOICE: "ACCESS_PURCHASE_CHOICE",
  ACCESS_PURCHASE_CLOSED: "ACCESS_PURCHASE_CLOSED",
  MEMBERSHIP_PURCHASE: "MEMBERSHIP_PURCHASE",
};

// ─────────────────────────────────────────────────────────────
// Small components
// ─────────────────────────────────────────────────────────────
const APP_CLIP_ACCENT = "#228be6";

const appClipStyles = {
  screen: {
    position: "relative",
    minHeight: "100dvh",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #ffffff 0%, #f1f3f5 48%, #ffffff 100%)",
  },
  contentLayer: {
    position: "relative",
    zIndex: 2,
  },
  glassCircle: {
    background: "rgba(255, 255, 255, 0.76)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    boxShadow: "0 20px 52px rgba(15, 23, 42, 0.10)",
    backdropFilter: "blur(22px) saturate(150%)",
    WebkitBackdropFilter: "blur(22px) saturate(150%)",
  },
  glassField: {
    minHeight: 58,
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "0 18px",
    borderRadius: 20,
    background: "rgba(255, 255, 255, 0.70)",
    border: "1px solid rgba(255, 255, 255, 0.58)",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.055)",
    backdropFilter: "blur(20px) saturate(145%)",
    WebkitBackdropFilter: "blur(20px) saturate(145%)",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 999,
    color: "var(--mantine-color-dimmed)",
    background: "rgba(255, 255, 255, 0.68)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    backdropFilter: "blur(18px) saturate(145%)",
    WebkitBackdropFilter: "blur(18px) saturate(145%)",
  },
  actionCard: {
    width: "100%",
    maxWidth: 500,
    padding: 22,
    borderRadius: 30,
    background: "rgba(255, 255, 255, 0.72)",
    border: "1px solid rgba(255, 255, 255, 0.56)",
    boxShadow: "0 22px 54px rgba(15, 23, 42, 0.08)",
    backdropFilter: "blur(28px) saturate(150%)",
    WebkitBackdropFilter: "blur(28px) saturate(150%)",
  },
  instructionCard: {
    width: "100%",
    padding: 16,
    borderRadius: 18,
    background: "rgba(255, 255, 255, 0.58)",
    border: "1px solid rgba(255, 255, 255, 0.48)",
  },
  prominentButton: {
    height: 58,
    borderRadius: 18,
    fontWeight: 700,
    fontSize: 16,
    boxShadow: "none",
  },
  secondaryButton: {
    minHeight: 52,
    borderRadius: 18,
    fontWeight: 600,
    background: "rgba(255, 255, 255, 0.64)",
    border: "1px solid rgba(34, 139, 230, 0.18)",
  },
};

const AppClipAmbientBackground = React.memo(
  function AppClipAmbientBackground() {
    return (
      <Box
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          overflow: "hidden",
          pointerEvents: "none",
          zIndex: 0,
        }}
      >
        <style>{`
          @keyframes slb-appclip-orb-one {
            from { transform: translate3d(-40px, -150px, 0); }
            to { transform: translate3d(-120px, -260px, 0); }
          }
          @keyframes slb-appclip-orb-two {
            from { transform: translate3d(60px, 120px, 0); }
            to { transform: translate3d(140px, 240px, 0); }
          }
          @keyframes slb-appclip-orb-three {
            from { transform: translate3d(40px, -220px, 0); }
            to { transform: translate3d(120px, -120px, 0); }
          }
          .slb-appclip-phone .PhoneInputCountry {
            margin-right: 12px;
          }
          .slb-appclip-phone .PhoneInputCountrySelectArrow {
            opacity: 0.5;
          }
          .slb-appclip-phone .PhoneInputInput,
          .slb-appclip-phone .mantine-TextInput-input {
            border: 0 !important;
            outline: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
            padding: 0 !important;
            min-height: 22px !important;
            height: auto !important;
            font-size: 17px !important;
            font-weight: 500 !important;
          }
          .slb-appclip-phone .mantine-TextInput-root,
          .slb-appclip-phone .mantine-TextInput-wrapper {
            flex: 1;
            width: 100%;
          }
          @media (prefers-reduced-motion: reduce) {
            .slb-appclip-orb { animation: none !important; }
          }
        `}</style>

        <Box
          className="slb-appclip-orb"
          style={{
            position: "absolute",
            width: 340,
            height: 340,
            left: "8%",
            top: "8%",
            borderRadius: "50%",
            background: "rgba(34, 139, 230, 0.18)",
            filter: "blur(70px)",
            animation: "slb-appclip-orb-one 8s ease-in-out infinite alternate",
          }}
        />

        <Box
          className="slb-appclip-orb"
          style={{
            position: "absolute",
            width: 320,
            height: 320,
            right: "4%",
            bottom: "3%",
            borderRadius: "50%",
            background: "rgba(59, 130, 246, 0.12)",
            filter: "blur(70px)",
            animation: "slb-appclip-orb-two 10s ease-in-out infinite alternate",
          }}
        />

        <Box
          className="slb-appclip-orb"
          style={{
            position: "absolute",
            width: 220,
            height: 220,
            right: "20%",
            top: "12%",
            borderRadius: "50%",
            background: "rgba(255, 255, 255, 0.74)",
            filter: "blur(55px)",
            animation:
              "slb-appclip-orb-three 7s ease-in-out infinite alternate",
          }}
        />

        <Box
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255, 255, 255, 0.08)",
            backdropFilter: "blur(2px)",
            WebkitBackdropFilter: "blur(2px)",
          }}
        />
      </Box>
    );
  },
);

function AppClipStatusScaffold({ children }) {
  return (
    <Box style={appClipStyles.screen}>
      <AppClipAmbientBackground />

      <Center
        style={{
          ...appClipStyles.contentLayer,
          minHeight: "100dvh",
          padding: "24px 20px",
        }}
      >
        <Box
          style={{
            width: "100%",
            maxWidth: 460,
          }}
        >
          {children}
        </Box>
      </Center>
    </Box>
  );
}

function AppClipInfoPill({ icon, text }) {
  return (
    <Box style={appClipStyles.pill}>
      {icon}
      <Text size="xs" fw={600} style={{ lineHeight: 1 }}>
        {text}
      </Text>
    </Box>
  );
}

function AppClipStatusIcon({ children, tint = APP_CLIP_ACCENT }) {
  return (
    <Center
      style={{
        width: 132,
        height: 132,
        borderRadius: "50%",
        color: tint,
        ...appClipStyles.glassCircle,
      }}
    >
      {children}
    </Center>
  );
}

const UnlockedLottie = React.memo(
  function UnlockedLottie() {
    const lottieRef = useRef(null);

    useEffect(() => {
      lottieRef.current?.play?.();
    }, []);

    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={unlockedAnimation}
        loop={false}
        autoplay
        style={S.lottieUnlocked}
      />
    );
  },
  () => true,
);

const EnterOtpView = React.memo(function EnterOtpView({
  onConfirmOTP,
  verificationCodeErr,
  phoneNumber,
  onBack,
}) {
  const [verificationCode, setVerificationCode] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus?.();
  }, []);

  const onConfirm = () => {
    if (verificationCode.length !== 6) return;
    onConfirmOTP(verificationCode);
  };

  return (
    <AppClipStatusScaffold>
      <Flex
        direction="column"
        style={{
          minHeight: "calc(100dvh - 48px)",
        }}
      >
        <Button
          variant="subtle"
          color="gray"
          px={0}
          onClick={onBack}
          style={{
            alignSelf: "flex-start",
            color: "var(--mantine-color-dimmed)",
          }}
          leftSection={<FaChevronLeft size={12} />}
        >
          Back
        </Button>

        <Box style={{ flex: 1 }} />

        <Box style={{ width: "100%", maxWidth: 420 }}>
          <Title
            order={1}
            style={{
              fontSize: "clamp(34px, 8vw, 36px)",
              lineHeight: 1.05,
              letterSpacing: "-0.035em",
              fontWeight: 800,
            }}
          >
            Enter code
          </Title>

          <Text c="dimmed" size="sm" mt={10}>
            We sent a 6-digit code to {phoneNumber || "your phone"}
          </Text>

          <Box
            mt={22}
            onClick={() => inputRef.current?.focus?.()}
            style={{
              position: "relative",
              height: 78,
              padding: "13px 14px",
              borderRadius: 22,
              cursor: "text",
              ...appClipStyles.glassCircle,
            }}
          >
            <input
              ref={inputRef}
              value={verificationCode}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label="Verification code"
              onChange={(event) => {
                const digits = event.target.value
                  .replace(/\D/g, "")
                  .slice(0, 6);
                setVerificationCode(digits);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") onConfirm();
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                opacity: 0.01,
                border: 0,
                outline: 0,
                cursor: "text",
              }}
            />

            <Flex gap={10} h="100%">
              {Array.from({ length: 6 }).map((_, index) => {
                const isActive =
                  verificationCode.length === index ||
                  (verificationCode.length >= 6 && index === 5);

                return (
                  <Center
                    key={index}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: 52,
                      borderRadius: 14,
                      background: "rgba(255, 255, 255, 0.54)",
                      border: isActive
                        ? `1.5px solid ${APP_CLIP_ACCENT}`
                        : "1.5px solid transparent",
                      fontSize: 24,
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                  >
                    {verificationCode[index] || ""}
                  </Center>
                );
              })}
            </Flex>
          </Box>
        </Box>

        <Box style={{ flex: 1 }} />

        <Box style={{ width: "100%", maxWidth: 420 }}>
          {verificationCodeErr && (
            <Text c="red" size="sm" fw={500} mb="sm">
              {verificationCodeErr}
            </Text>
          )}

          <Button
            fullWidth
            onClick={onConfirm}
            disabled={verificationCode.length !== 6}
            style={appClipStyles.prominentButton}
          >
            Verify code
          </Button>

          <Button
            variant="subtle"
            color="gray"
            fullWidth
            mt={6}
            onClick={onBack}
            style={{ color: "var(--mantine-color-dimmed)" }}
          >
            Change phone number
          </Button>
        </Box>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const UnlockingAnimation = React.memo(function UnlockingAnimation() {
  return (
    <Flex justify="center" direction="column" style={{ width: "100%" }}>
      <Flex
        style={{
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <Lottie style={S.lottieSignal} animationData={signalAnimation} loop />

        <Flex
          style={{
            position: "absolute",
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Flex direction="column" align="center" w="100%">
            <Lottie
              style={S.lottieUnlocking}
              animationData={unlockingAnimation}
              loop
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

const LoadingView = React.memo(function LoadingView() {
  return (
    <AppClipStatusScaffold>
      <Center style={{ minHeight: "calc(100dvh - 48px)" }}>
        <UnlockingAnimation />
      </Center>
    </AppClipStatusScaffold>
  );
});

const UnlockingView = React.memo(function UnlockingView() {
  return (
    <AppClipStatusScaffold>
      <Center style={{ minHeight: "calc(100dvh - 48px)" }}>
        <UnlockingAnimation />
      </Center>
    </AppClipStatusScaffold>
  );
});

const NoLockView = React.memo(function NoLockView({
  title = "Invalid URL",
  message = "We cannot find any items for this URL, please try again with the valid URL",
}) {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon>
          <FaQrcode size={50} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          {title}
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 360 }}>
          {message}
        </Text>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const NoLocationView = React.memo(function NoLocationView({
  onRetry,
  browserInfo,
  permissionState,
  locationErrorCode,
}) {
  const [showHelp, setShowHelp] = useState(
    locationErrorCode === 1 || permissionState === "denied",
  );

  useEffect(() => {
    if (locationErrorCode === 1 || permissionState === "denied") {
      setShowHelp(true);
    }
  }, [locationErrorCode, permissionState]);

  const help = useMemo(
    () =>
      getLocationHelpContent({
        browserInfo,
        permissionState,
        errorCode: locationErrorCode,
      }),
    [browserInfo, permissionState, locationErrorCode],
  );

  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon tint="#fa5252">
          <FaMapMarkerAlt size={48} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          {help.title}
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 380 }}>
          {help.message}
        </Text>

        <Flex gap="sm" wrap="wrap" justify="center" w="100%">
          <Button
            variant="default"
            onClick={() => setShowHelp((value) => !value)}
            style={appClipStyles.secondaryButton}
          >
            {showHelp ? "Hide help" : help.buttonLabel}
          </Button>

          <Button
            variant="outline"
            onClick={onRetry}
            style={{
              ...appClipStyles.secondaryButton,
              color: APP_CLIP_ACCENT,
            }}
          >
            Try again
          </Button>
        </Flex>

        {showHelp && (
          <Box style={{ ...appClipStyles.actionCard, maxWidth: 420 }}>
            <Text fw={700} mb="sm">
              Steps
            </Text>

            {help.steps.map((step, index) => (
              <Text
                key={index}
                size="sm"
                c="dimmed"
                mb={index === help.steps.length - 1 ? 0 : 8}
              >
                {index + 1}. {step}
              </Text>
            ))}
          </Box>
        )}
      </Flex>
    </AppClipStatusScaffold>
  );
});

const NotNearLocationView = React.memo(function NotNearLocationView() {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon tint="#fa5252">
          <FaMapMarkerAlt size={48} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Invalid Location
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 360 }}>
          We found that you are not near the location of the item. Please try
          again later.
        </Text>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const ExpiredView = React.memo(function ExpiredView() {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <Lottie
          animationData={errorAnimation}
          style={{ width: 124, height: 124 }}
          loop={false}
        />

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Access Expired
        </Title>

        <Text c="dimmed" fw={600} ta="center">
          Your temporary access period has ended.
        </Text>

        <Box style={appClipStyles.instructionCard}>
          <Text ta="center">
            If you need access again, scan the QR code or call support.
          </Text>
        </Box>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const OutsideOpeningHoursView = React.memo(function OutsideOpeningHoursView({
  availability,
  site,
  supportPhone,
}) {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon tint="#f59f00">
          <FaRegClock size={48} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Outside opening hours
        </Title>

        <Text
          fw={600}
          ta="center"
          style={{
            maxWidth: 390,
            fontSize: 17,
            lineHeight: 1.5,
          }}
        >
          This door cannot be unlocked outside the opening hours.
        </Text>

        {availability?.hasSchedule && (
          <Box
            style={{
              ...appClipStyles.actionCard,
              maxWidth: 420,
            }}
          >
            <Flex direction="column" gap={14}>
              {site?.title && (
                <Text fw={800} ta="center">
                  {site.title}
                </Text>
              )}

              <Flex justify="space-between" gap="md" align="flex-start">
                <Text size="sm" c="dimmed" fw={600}>
                  Today
                </Text>

                <Text size="sm" fw={700} ta="right" style={{ maxWidth: 245 }}>
                  {availability.todayHoursLabel || "Closed today"}
                </Text>
              </Flex>

              <Flex justify="space-between" gap="md" align="flex-start">
                <Text size="sm" c="dimmed" fw={600}>
                  Next opening
                </Text>

                <Text size="sm" fw={700} ta="right" style={{ maxWidth: 245 }}>
                  {availability.nextOpeningLabel ||
                    "No upcoming opening time is configured"}
                </Text>
              </Flex>

              <Text size="xs" c="dimmed" ta="center">
                Times are shown in{" "}
                {availability.timezone || site?.timezone || "the site timezone"}
                .
              </Text>
            </Flex>
          </Box>
        )}

        {supportPhone && (
          <Button
            component="a"
            href={`tel:${supportPhone}`}
            variant="outline"
            style={{
              ...appClipStyles.secondaryButton,
              color: APP_CLIP_ACCENT,
            }}
          >
            Call support
          </Button>
        )}
      </Flex>
    </AppClipStatusScaffold>
  );
});

const InputDetailsView = React.memo(function InputDetailsView({
  site,
  lock,
  isCareZenBusiness = false,
  isBanned,
  logoAnimation,
  isLogoAnimationLoading,
  name,
  setName,
  phoneNumber,
  setPhoneNumber,
  errorMsg,
  supportPhone,
  onSubmit,
}) {
  const backgroundUrl = useMemo(
    () => (isCareZenBusiness ? czbackground : site?.image_remote_url || null),
    [isCareZenBusiness, site?.image_remote_url],
  );

  const welcomeTitle = isCareZenBusiness ? "CareZen" : site?.title;
  const itemTitle = isCareZenBusiness ? "Virtual Care Pod" : lock?.title;

  const endTillCloseTime = truthy(lock?.end_til_close_time);

  const formatHirePrice = (value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    const formatter = new Intl.NumberFormat("en-AU", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return `${formatter.format(numericValue)} AUD`;
  };

  const formattedDurationPrice = useMemo(
    () => formatHirePrice(lock?.hire_cost_base),
    [lock?.hire_cost_base],
  );

  const formattedPersonPrice = useMemo(
    () => formatHirePrice(lock?.hire_cost_base_person),
    [lock?.hire_cost_base_person],
  );

  const durationPriceLabel = formattedDurationPrice
    ? lock?.charge_per_session
      ? `${formattedDurationPrice} per session`
      : lock?.end_til_close_time
        ? null
        : `${formattedDurationPrice} per minute`
    : null;

  const personPriceLabel = formattedPersonPrice
    ? `${formattedPersonPrice} per person`
    : null;

  const maxMins = useMemo(() => {
    if (endTillCloseTime) {
      return null;
    }

    const value = Number(lock?.hire_max_length);
    return Number.isFinite(value) && value > 0 ? `${value} minutes` : null;
  }, [endTillCloseTime, lock?.hire_max_length]);

  const hasDurationPrice = Number(lock?.hire_cost_base || 0) > 0;
  const hasPersonPrice = Number(lock?.hire_cost_base_person || 0) > 0;

  // AJAX items always show the preview. Otherwise show it when at least
  // one configured hire charge is greater than zero.
  const showPriceTimePreview = Boolean(
    lock?.is_ajax_item || hasDurationPrice || hasPersonPrice,
  );

  const logoSize = 148;

  return (
    <Box style={{ ...appClipStyles.screen, overflowY: "auto" }}>
      <AppClipAmbientBackground />

      <Box
        style={{
          ...appClipStyles.contentLayer,
          width: "100%",
          maxWidth: 700,
          minHeight: "100dvh",
          marginInline: "auto",
          paddingBottom: 40,
        }}
      >
        <Box
          style={{
            position: "relative",
            width: "100%",
            height: 264,
            paddingTop: 0,
          }}
        >
          <Box
            style={{
              position: "relative",
              width: "100%",
              height: 200,
              overflow: "hidden",
              background: "#e9ecef",
            }}
          >
            {backgroundUrl && (
              <Image
                src={backgroundUrl}
                alt=""
                fit="cover"
                w="100%"
                h={200}
                style={{ display: "block" }}
              />
            )}

            <Box
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(0,0,0,0.22) 100%)",
              }}
            />
          </Box>

          <Center
            style={{
              position: "absolute",
              left: "50%",
              bottom: 0,
              width: logoSize,
              height: logoSize,
              borderRadius: "50%",
              transform: "translateX(-50%)",
              ...appClipStyles.glassCircle,
            }}
          >
            {isLogoAnimationLoading ? (
              <Lottie
                animationData={imagePlaceholder}
                loop
                autoplay
                style={{ width: 118, height: 118 }}
              />
            ) : logoAnimation ? (
              <Lottie
                animationData={logoAnimation}
                loop={false}
                autoplay
                style={{ width: 110, height: 110 }}
              />
            ) : null}
          </Center>
        </Box>

        <Box px={20} pt={35}>
          <Box ta="center">
            <Title
              order={1}
              style={{
                fontSize: "clamp(28px, 7vw, 32px)",
                lineHeight: 1.08,
                letterSpacing: "-0.035em",
                fontWeight: 800,
              }}
            >
              Welcome to {welcomeTitle}
            </Title>

            {itemTitle && (
              <Text c="dimmed" size="sm" mt={8}>
                {itemTitle}
              </Text>
            )}
          </Box>

          {showPriceTimePreview &&
            (durationPriceLabel || personPriceLabel || maxMins) && (
              <Flex
                justify="center"
                align="center"
                gap={10}
                wrap="wrap"
                mt={18}
              >
                {durationPriceLabel && (
                  <AppClipInfoPill
                    icon={<FaDollarSign size={13} />}
                    text={durationPriceLabel}
                  />
                )}

                {personPriceLabel && (
                  <AppClipInfoPill
                    icon={<FaUser size={13} />}
                    text={personPriceLabel}
                  />
                )}

                {maxMins && (
                  <AppClipInfoPill
                    icon={<FaRegClock size={13} />}
                    text={maxMins}
                  />
                )}
              </Flex>
            )}

          <Box
            mt={18}
            mx="auto"
            style={{
              width: "100%",
              maxWidth: 500,
            }}
          >
            <Flex direction="column" gap={12}>
              <Box style={appClipStyles.glassField}>
                <FaUser
                  size={15}
                  style={{
                    flex: "0 0 auto",
                    color: "var(--mantine-color-dimmed)",
                  }}
                />

                <TextInput
                  variant="unstyled"
                  placeholder="Enter Name (Optional)"
                  value={name}
                  onChange={(event) => setName(event.currentTarget.value)}
                  styles={{
                    root: { flex: 1 },
                    wrapper: { width: "100%" },
                    input: {
                      minHeight: 22,
                      padding: 0,
                      fontSize: 17,
                      fontWeight: 500,
                      lineHeight: 1.3,
                      background: "transparent",
                    },
                  }}
                />
              </Box>

              <Box style={appClipStyles.glassField}>
                <PhoneInput
                  className="slb-appclip-phone"
                  international
                  defaultCountry="AU"
                  countryCallingCodeEditable={false}
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={setPhoneNumber}
                  inputComponent={TextInput}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    fontSize: 17,
                    fontWeight: 500,
                  }}
                  inputProps={{
                    variant: "unstyled",
                    styles: {
                      root: { flex: 1 },
                      wrapper: { width: "100%" },
                      input: {
                        minHeight: 22,
                        padding: 0,
                        fontSize: 17,
                        fontWeight: 500,
                        lineHeight: 1.3,
                        background: "transparent",
                      },
                    },
                  }}
                />
              </Box>
            </Flex>

            {errorMsg && (
              <Text c="red" size="sm" fw={500} mt="sm">
                {errorMsg}
              </Text>
            )}

            {isBanned && supportPhone && (
              <Button
                component="a"
                href={`tel:${supportPhone}`}
                color="green"
                fullWidth
                mt="md"
                style={appClipStyles.prominentButton}
              >
                Call Support
              </Button>
            )}

            <Button
              fullWidth
              mt="md"
              onClick={onSubmit}
              disabled={!phoneNumber}
              style={appClipStyles.prominentButton}
            >
              {lock?.is_ajax_item ? "Next" : "Continue"}
            </Button>
          </Box>

          <Text
            size="xs"
            c="dimmed"
            ta="center"
            mt={18}
            style={{ lineHeight: 1.55 }}
          >
            By continuing, you agree to our{" "}
            <a href="https://www.autech.com.au/unlockwc-privacy">
              Privacy Policy
            </a>{" "}
            and <a href="https://www.autech.com.au/unlockwc-tc">T&amp;Cs</a>.
          </Text>
        </Box>
      </Box>
    </Box>
  );
});

const UnlockedView = React.memo(function UnlockedView({
  isAjaxItem,
  isHireStarted,
  onStartHire,
  startingHire,
  onUnlockAgain,
  unlocking,
  remainingTime,
  hireMaxLength,
  sessionPurchasedTime = 0,
  errorMsg,
  setAuthFlow,
  AuthFlowRef,
  showConfetti,
  setHireMode,
  supportPhone,
  onReportDamage,
  hasReportDamage,
}) {
  const extendAvailable = useMemo(
    () =>
      Math.max(
        0,
        Number(hireMaxLength || 0) - Number(sessionPurchasedTime || 0),
      ),
    [hireMaxLength, sessionPurchasedTime],
  );

  const primaryTitle = isAjaxItem ? "Item Hired" : "Door Unlocked";
  const showTimer = !isAjaxItem || (isAjaxItem && isHireStarted);

  return (
    <Box style={appClipStyles.screen}>
      <AppClipAmbientBackground />

      {showConfetti && (
        <Confetti recycle={false} style={{ position: "fixed", inset: 0 }} />
      )}

      <Center
        style={{
          ...appClipStyles.contentLayer,
          minHeight: "100dvh",
          padding: "32px 20px",
        }}
      >
        <Flex direction="column" align="center" gap={18} w="100%">
          <UnlockedLottie />

          <Box style={appClipStyles.actionCard}>
            <Flex direction="column" align="center" gap={18}>
              <Title
                order={2}
                ta="center"
                style={{
                  fontSize: 30,
                  lineHeight: 1.08,
                  letterSpacing: "-0.03em",
                }}
              >
                {primaryTitle}
              </Title>

              {showTimer && (
                <Text fw={600} c="dimmed" ta="center">
                  Remaining Time: {formatRemainingTime(remainingTime)}
                </Text>
              )}

              <Box style={appClipStyles.instructionCard}>
                {isAjaxItem ? (
                  <Flex direction="column" gap={8}>
                    <Text>
                      1. Start your hire by pressing the green button below.
                    </Text>
                    <Text>
                      2. Once your hire is started, you can use the facility for
                      the paid amount of time.
                    </Text>
                    <Text>
                      3. Keep your phone handy in case you need support.
                    </Text>
                  </Flex>
                ) : (
                  <Flex direction="column" gap={8}>
                    <Text>You now have temporary access to the facility.</Text>
                    <Text>This access is valid for a limited time.</Text>
                    <Text>Keep your phone handy in case you need support.</Text>
                  </Flex>
                )}
              </Box>

              {isAjaxItem && !isHireStarted && (
                <Button
                  fullWidth
                  color="green"
                  onClick={onStartHire}
                  loading={startingHire}
                  disabled={startingHire}
                  style={appClipStyles.prominentButton}
                >
                  Start Hire
                </Button>
              )}

              <Flex gap={12} w="100%" wrap="wrap">
                {!isAjaxItem && (
                  <Button
                    color="green"
                    onClick={onUnlockAgain}
                    loading={unlocking}
                    disabled={unlocking}
                    style={{
                      ...appClipStyles.prominentButton,
                      flex: "1 1 180px",
                      minHeight: 52,
                    }}
                  >
                    Unlock Again
                  </Button>
                )}

                {extendAvailable > 0 && isAjaxItem && (
                  <Button
                    variant="default"
                    color="orange"
                    onClick={() => {
                      setHireMode("extend");
                      setAuthFlow(AuthFlowRef.HIRE_SELECT);
                    }}
                    style={{
                      ...appClipStyles.secondaryButton,
                      flex: "1 1 150px",
                      color: "#f08c00",
                      borderColor: "rgba(240, 140, 0, 0.22)",
                    }}
                  >
                    Extend
                  </Button>
                )}

                {supportPhone && (
                  <Button
                    variant="default"
                    onClick={() => window.open(`tel://${supportPhone}`)}
                    style={{
                      ...appClipStyles.secondaryButton,
                      flex: "1 1 150px",
                      color: APP_CLIP_ACCENT,
                    }}
                  >
                    Call Support
                  </Button>
                )}
              </Flex>

              {hasReportDamage && (
                <Button
                  variant="default"
                  color="red"
                  fullWidth
                  onClick={onReportDamage}
                  style={{
                    ...appClipStyles.secondaryButton,
                    color: "#fa5252",
                    borderColor: "rgba(250, 82, 82, 0.22)",
                  }}
                >
                  Report Damage
                </Button>
              )}

              {errorMsg && (
                <Text c="red" size="sm" fw={500} w="100%">
                  {errorMsg}
                </Text>
              )}
            </Flex>
          </Box>
        </Flex>
      </Center>
    </Box>
  );
});

const MembershipCheckoutForm = React.memo(function MembershipCheckoutForm({
  amountCents,
  businessName,
  purchaseOpen = true,
  onBeforePayment,
  onComplete,
  onError,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isPaying, setIsPaying] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || isPaying) {
      return;
    }

    const purchaseContext = onBeforePayment?.();

    if (!purchaseOpen || purchaseContext === false) {
      onError?.("New memberships can only be purchased during opening hours.");
      return;
    }

    setIsPaying(true);

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {},
        redirect: "if_required",
      });

      if (result.error) {
        throw new Error(result.error.message || "Payment unsuccessful.");
      }

      const completed = await onComplete(purchaseContext);

      if (!completed) {
        setIsPaying(false);
      }
    } catch (error) {
      setIsPaying(false);
      onError?.(error?.message || "Payment unsuccessful.");
    }
  };

  const priceLabel = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(Math.max(0, Number(amountCents || 0)) / 100);

  return (
    <form onSubmit={handleSubmit}>
      <Flex direction="column" gap={14}>
        <PaymentElement
          options={{
            business: {
              name: businessName || "Membership",
            },
          }}
        />

        <motion.div whileTap={{ scale: 0.985 }}>
          <Button
            type="submit"
            fullWidth
            loading={isPaying}
            disabled={!stripe || !elements || isPaying || !purchaseOpen}
            rightSection={purchaseOpen ? <FaArrowRight size={12} /> : null}
            bg="green"
            style={{
              ...appClipStyles.prominentButton,
              height: 56,
            }}
          >
            {purchaseOpen ? `Pay ${priceLabel}` : "Purchase unavailable"}
          </Button>
        </motion.div>
      </Flex>
    </form>
  );
});

const MembershipPaymentView = React.memo(function MembershipPaymentView({
  session,
  purchaseOpen = true,
  onBeforePayment,
  onComplete,
  onBack,
  onError,
  errorMsg,
}) {
  const stripePromise = useMemo(
    () => loadStripe(session.publishableKey),
    [session.publishableKey],
  );

  const membershipTitle =
    getMembershipField(session.membership, "title", "Title") || "Membership";

  const membershipLength = toNumber(
    getMembershipField(session.membership, "length", "Length"),
    0,
  );

  const amountLabel = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
  }).format(Math.max(0, Number(session.amountCents || 0)) / 100);

  return (
    <Box style={{ ...appClipStyles.screen, overflowY: "auto" }}>
      <AppClipAmbientBackground />

      <Box
        style={{
          ...appClipStyles.contentLayer,
          width: "100%",
          maxWidth: 600,
          minHeight: "100dvh",
          marginInline: "auto",
          padding: "24px 18px 40px",
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          style={{ width: "fit-content" }}
        >
          <Button
            variant="subtle"
            color="gray"
            px={4}
            onClick={onBack}
            leftSection={<FaChevronLeft size={12} />}
            style={{
              height: 42,
              borderRadius: 14,
              color: "var(--mantine-color-dimmed)",
            }}
          >
            Back
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.42,
            delay: 0.05,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <Box mt={22}>
            <Text
              size="xs"
              fw={800}
              tt="uppercase"
              c={"#7950f2"}
              style={{ letterSpacing: "0.1em" }}
            >
              Membership checkout
            </Text>

            <Title
              order={1}
              mt={7}
              style={{
                fontSize: "clamp(30px, 8vw, 40px)",
                lineHeight: 1.04,
                letterSpacing: "-0.04em",
                fontWeight: 850,
              }}
            >
              Complete your purchase
            </Title>

            <Text c="dimmed" mt={10} style={{ lineHeight: 1.5 }}>
              Review your membership, then securely complete payment below.
            </Text>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18, scale: 0.99 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{
            duration: 0.42,
            delay: 0.12,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <Box
            mt={22}
            style={{
              position: "relative",
              overflow: "hidden",
              padding: 18,
              borderRadius: 24,
              background:
                "linear-gradient(140deg, rgba(243, 240, 255, 0.95) 0%, rgba(255, 255, 255, 0.79) 58%, rgba(255, 255, 255, 0.62) 100%)",
              border: "1px solid rgba(255, 255, 255, 0.72)",
              boxShadow: "0 16px 40px rgba(15, 23, 42, 0.07)",
              backdropFilter: "blur(24px) saturate(150%)",
              WebkitBackdropFilter: "blur(24px) saturate(150%)",
            }}
          >
            <Box
              aria-hidden="true"
              style={{
                position: "absolute",
                width: 150,
                height: 150,
                borderRadius: "50%",
                right: -62,
                top: -78,
                background: "rgba(121, 80, 242, 0.13)",
                filter: "blur(102px)",
              }}
            />

            <Flex
              justify="space-between"
              align="center"
              gap={16}
              style={{ position: "relative", zIndex: 1 }}
            >
              <Flex gap={12} align="center" style={{ minWidth: 0 }}>
                <Center
                  style={{
                    width: 46,
                    height: 46,
                    flexShrink: 0,
                    borderRadius: 15,
                    color: "#7950f2",
                    background: "rgba(121, 80, 242, 0.11)",
                    border: "1px solid rgba(121, 80, 242, 0.11)",
                  }}
                >
                  <FaIdCard size={19} />
                </Center>

                <Box style={{ minWidth: 0 }}>
                  <Text fw={800} style={{ fontSize: 17, lineHeight: 1.2 }}>
                    {membershipTitle}
                  </Text>

                  {membershipLength > 0 && (
                    <Text c="dimmed" size="sm" mt={3}>
                      {membershipLength} day{membershipLength === 1 ? "" : "s"}{" "}
                      access
                    </Text>
                  )}
                </Box>
              </Flex>

              <Box
                style={{
                  flexShrink: 0,
                  padding: "8px 11px",
                  borderRadius: 999,
                  background: "rgba(255, 255, 255, 0.76)",
                  border: "1px solid rgba(34, 139, 230, 0.10)",
                }}
              >
                <Text fw={850} style={{ fontSize: 16 }}>
                  {amountLabel}
                </Text>
              </Box>
            </Flex>
          </Box>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.45,
            delay: 0.18,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <Box mt={20}>
            <Flex align="center" gap={9} mb={12}>
              <Center
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 10,
                  color: "green",
                  background: "rgba(47, 179, 68, 0.10)",
                }}
              >
                <FaDollarSign size={13} />
              </Center>

              <Box>
                <Text fw={750} size="sm">
                  Secure payment
                </Text>
                <Text size="xs" c="dimmed" mt={1}>
                  Payment details are processed securely by Stripe.
                </Text>
              </Box>
            </Flex>

            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: session.clientSecret,
                appearance: {
                  variables: {
                    borderRadius: "14px",
                    spacingGridRow: "12px",
                  },
                },
              }}
            >
              <MembershipCheckoutForm
                amountCents={session.amountCents}
                businessName={membershipTitle}
                purchaseOpen={purchaseOpen}
                onBeforePayment={onBeforePayment}
                onComplete={onComplete}
                onError={onError}
              />
            </Elements>
          </Box>
        </motion.div>

        {errorMsg && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Text c="red" size="sm" fw={600} mt="md">
              {errorMsg}
            </Text>
          </motion.div>
        )}
      </Box>
    </Box>
  );
});

const MembershipPurchaseView = React.memo(function MembershipPurchaseView({
  memberships,
  purchasingMembershipId,
  onPurchase,
  onBack,
  errorMsg,
  appleLink = "",
  googleLink = "",
}) {
  const hasApple = typeof appleLink === "string" && appleLink.trim().length > 0;
  const hasGoogle =
    typeof googleLink === "string" && googleLink.trim().length > 0;

  return (
    <Box style={{ ...appClipStyles.screen, overflowY: "auto" }}>
      <AppClipAmbientBackground />

      <Box
        style={{
          ...appClipStyles.contentLayer,
          width: "100%",
          maxWidth: 600,
          minHeight: "100dvh",
          marginInline: "auto",
          padding: "24px 18px 42px",
        }}
      >
        {onBack && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.28, ease: "easeOut" }}
            style={{ width: "fit-content" }}
          >
            <Button
              variant="subtle"
              color="gray"
              px={4}
              onClick={onBack}
              leftSection={<FaChevronLeft size={12} />}
              style={{
                height: 42,
                borderRadius: 14,
                color: "var(--mantine-color-dimmed)",
              }}
            >
              Back
            </Button>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.42,
            delay: 0.05,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <Box mt={onBack ? 22 : 0}>
            <Text
              size="xs"
              fw={800}
              tt="uppercase"
              c={"#7950f2"}
              style={{ letterSpacing: "0.1em" }}
            >
              Membership access
            </Text>

            <Title
              order={1}
              mt={7}
              style={{
                fontSize: "clamp(30px, 8vw, 40px)",
                lineHeight: 1.04,
                letterSpacing: "-0.04em",
                fontWeight: 850,
              }}
            >
              Choose your membership
            </Title>

            <Text
              c="dimmed"
              mt={11}
              mb={24}
              style={{
                maxWidth: 500,
                fontSize: 16,
                lineHeight: 1.52,
                fontWeight: 500,
              }}
            >
              Your phone number has been verified. Select a membership below to
              continue and unlock this item.
            </Text>
          </Box>
        </motion.div>

        <Flex direction="column" gap={14}>
          {memberships.map((membership, index) => {
            const membershipId = getMembershipId(membership);
            const title =
              getMembershipField(membership, "title", "Title") || "Membership";
            const length = toNumber(
              getMembershipField(membership, "length", "Length"),
              0,
            );
            const isLoading =
              String(purchasingMembershipId) === String(membershipId);

            return (
              <motion.div
                key={membershipId || title}
                initial={{ opacity: 0, y: 22, scale: 0.985 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.43,
                  delay: 0.11 + index * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{
                  y: -3,
                  scale: 1.005,
                  transition: { duration: 0.18 },
                }}
              >
                <Box
                  style={{
                    position: "relative",
                    overflow: "hidden",
                    width: "100%",
                    padding: 20,
                    borderRadius: 28,
                    background:
                      "linear-gradient(140deg, rgba(243, 240, 255, 0.95) 0%, rgba(255, 255, 255, 0.79) 58%, rgba(255, 255, 255, 0.62) 100%)",
                    border: "1px solid rgba(255, 255, 255, 0.72)",
                    boxShadow: "0 18px 46px rgba(15, 23, 42, 0.075)",
                    backdropFilter: "blur(26px) saturate(150%)",
                    WebkitBackdropFilter: "blur(26px) saturate(150%)",
                  }}
                >
                  <Box
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      width: 170,
                      height: 170,
                      borderRadius: "50%",
                      right: -68,
                      top: -88,
                      background: "rgba(121, 80, 242, 0.13)",
                      filter: "blur(102px)",
                    }}
                  />

                  <Flex
                    justify="space-between"
                    align="flex-start"
                    gap={16}
                    style={{ position: "relative", zIndex: 1 }}
                  >
                    <Flex gap={13} align="center" style={{ minWidth: 0 }}>
                      <Center
                        style={{
                          width: 50,
                          height: 50,
                          flexShrink: 0,
                          borderRadius: 16,
                          color: "#7950f2",
                          background: "rgba(121, 80, 242, 0.11)",
                          border: "1px solid rgba(121, 80, 242, 0.11)",
                        }}
                      >
                        <FaIdCard size={21} />
                      </Center>

                      <Box style={{ minWidth: 0 }}>
                        <Text
                          fw={850}
                          style={{
                            fontSize: 19,
                            lineHeight: 1.18,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {title}
                        </Text>

                        {length > 0 && (
                          <Flex align="center" gap={6} mt={5}>
                            <FaRegClock
                              size={11}
                              style={{ color: "var(--mantine-color-dimmed)" }}
                            />
                            <Text c="dimmed" size="sm" fw={550}>
                              {length} day{length === 1 ? "" : "s"} access
                            </Text>
                          </Flex>
                        )}
                      </Box>
                    </Flex>

                    <Box
                      style={{
                        flexShrink: 0,
                        padding: "8px 11px",
                        borderRadius: 999,
                        background: "rgba(255, 255, 255, 0.78)",
                        border: "1px solid rgba(34, 139, 230, 0.10)",
                      }}
                    >
                      <Text fw={850} style={{ fontSize: 16 }}>
                        {formatMembershipPrice(membership)}
                      </Text>
                    </Box>
                  </Flex>

                  <Box
                    mt={18}
                    style={{
                      position: "relative",
                      zIndex: 1,
                      padding: "12px 14px",
                      borderRadius: 15,
                      background: "rgba(121, 80, 242, 0.065)",
                    }}
                  >
                    <Text size="sm" fw={700} style={{ color: "#5f3dc4" }}>
                      Purchase once and activate your membership immediately.
                    </Text>
                  </Box>

                  <motion.div whileTap={{ scale: 0.985 }}>
                    <Button
                      fullWidth
                      mt={16}
                      bg={"#7950f2"}
                      loading={isLoading}
                      disabled={purchasingMembershipId !== "" && !isLoading}
                      onClick={() => onPurchase(membership)}
                      rightSection={<FaArrowRight size={12} />}
                      style={{
                        ...appClipStyles.prominentButton,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      Purchase membership
                    </Button>
                  </motion.div>
                </Box>
              </motion.div>
            );
          })}
        </Flex>

        {memberships.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <Box
              style={{
                padding: 22,
                borderRadius: 26,
                background: "rgba(255, 255, 255, 0.72)",
                border: "1px solid rgba(255, 255, 255, 0.62)",
                boxShadow: "0 18px 44px rgba(15, 23, 42, 0.07)",
                backdropFilter: "blur(24px) saturate(150%)",
                WebkitBackdropFilter: "blur(24px) saturate(150%)",
              }}
            >
              <Center
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 16,
                  color: APP_CLIP_ACCENT,
                  background: "rgba(34, 139, 230, 0.10)",
                }}
              >
                <FaIdCard size={20} />
              </Center>

              <Text fw={800} mt={14}>
                No memberships are currently available.
              </Text>
              <Text c="dimmed" size="sm" mt={6}>
                Please use the full app or contact support.
              </Text>

              {(hasApple || hasGoogle) && (
                <Flex gap="md" wrap="wrap" mt="md">
                  {hasApple && (
                    <AppStoreButton url={appleLink} theme="dark" width="auto" />
                  )}
                  {hasGoogle && (
                    <GooglePlayButton
                      url={googleLink}
                      theme="dark"
                      width="auto"
                    />
                  )}
                </Flex>
              )}
            </Box>
          </motion.div>
        )}

        {errorMsg && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Text c="red" size="sm" fw={600} mt="md">
              {errorMsg}
            </Text>
          </motion.div>
        )}
      </Box>
    </Box>
  );
});

const AccessPurchaseClosedView = React.memo(function AccessPurchaseClosedView({
  availability,
  site,
  supportPhone,
  onCheckAgain,
  onBack,
}) {
  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <AppClipStatusIcon tint="#f59f00">
          <FaRegClock size={48} />
        </AppClipStatusIcon>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          Access purchases unavailable
        </Title>

        <Text
          c="dimmed"
          fw={500}
          ta="center"
          style={{
            maxWidth: 390,
          }}
        >
          New sessions and memberships can only be purchased during this
          facility's configured opening hours.
        </Text>

        <Box style={{ ...appClipStyles.actionCard, maxWidth: 420 }}>
          <Flex direction="column" gap={14}>
            <Flex justify="space-between" gap="md" align="center">
              <Text size="sm" c="dimmed" fw={600}>
                Local time
              </Text>

              <Text size="sm" fw={700}>
                {availability?.currentTimeLabel || "—"}
              </Text>
            </Flex>

            <Flex justify="space-between" gap="md" align="flex-start">
              <Text size="sm" c="dimmed" fw={600}>
                Today
              </Text>

              <Text
                size="sm"
                fw={700}
                ta="right"
                style={{
                  maxWidth: 250,
                }}
              >
                {availability?.todayHoursLabel || "Closed today"}
              </Text>
            </Flex>

            <Flex justify="space-between" gap="md" align="flex-start">
              <Text size="sm" c="dimmed" fw={600}>
                Next opening
              </Text>

              <Text
                size="sm"
                fw={700}
                ta="right"
                style={{
                  maxWidth: 250,
                }}
              >
                {availability?.nextOpeningLabel ||
                  "No upcoming opening time is configured"}
              </Text>
            </Flex>

            <Text size="xs" c="dimmed" ta="center" mt={4}>
              Times are shown in{" "}
              {availability?.timezone || site?.timezone || "the site timezone"}.
            </Text>
          </Flex>
        </Box>

        <Flex gap="sm" wrap="wrap" justify="center" w="100%">
          <Button
            variant="outline"
            onClick={onCheckAgain}
            style={{
              ...appClipStyles.secondaryButton,
              color: APP_CLIP_ACCENT,
            }}
          >
            Check again
          </Button>

          {onBack && (
            <Button
              variant="default"
              onClick={onBack}
              style={appClipStyles.secondaryButton}
            >
              Back
            </Button>
          )}

          {supportPhone && (
            <Button
              component="a"
              href={`tel:${supportPhone}`}
              variant="default"
              style={appClipStyles.secondaryButton}
            >
              Call support
            </Button>
          )}
        </Flex>
      </Flex>
    </AppClipStatusScaffold>
  );
});

const AccessPurchaseChoiceView = React.memo(function AccessPurchaseChoiceView({
  canPurchaseSession,
  canPurchaseMembership,
  sessionCost,
  customerCost,
  chargePerSession,
  endTillCloseTime,
  sessionPurchasesOpen = true,
  sessionPurchaseAvailability = null,
  onPurchaseSession,
  onPurchaseMembership,
  onBack,
  errorMsg,
}) {
  const formattedSessionCost = useMemo(() => {
    const value = Number(sessionCost);

    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }

    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(value);
  }, [sessionCost]);

  const formattedCustomerCost = useMemo(() => {
    const value = Number(customerCost);

    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }

    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 2,
    }).format(value);
  }, [customerCost]);

  const oneTimePriceLabel = formattedSessionCost
    ? chargePerSession
      ? formattedSessionCost
      : endTillCloseTime
        ? formattedSessionCost
        : `${formattedSessionCost}/min`
    : null;

  const perPersonPriceLabel = formattedCustomerCost
    ? chargePerSession
      ? `${formattedCustomerCost}/person`
      : `${formattedCustomerCost}/person/min`
    : null;

  const purchasesUnavailable = !sessionPurchasesOpen;

  const cardBase = {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    padding: 22,
    borderRadius: 28,
    border: "1px solid rgba(255, 255, 255, 0.72)",
    boxShadow: "0 18px 50px rgba(15, 23, 42, 0.075)",
    backdropFilter: "blur(26px) saturate(150%)",
    WebkitBackdropFilter: "blur(26px) saturate(150%)",
  };

  return (
    <Box style={{ ...appClipStyles.screen, overflowY: "auto" }}>
      <AppClipAmbientBackground />

      <Box
        style={{
          ...appClipStyles.contentLayer,
          width: "100%",
          maxWidth: 620,
          minHeight: "100dvh",
          marginInline: "auto",
          padding: "24px 18px 46px",
        }}
      >
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.28, ease: "easeOut" }}
          whileTap={{ scale: 0.97 }}
          style={{ width: "fit-content" }}
        >
          <Button
            variant="subtle"
            color="gray"
            px={4}
            onClick={onBack}
            leftSection={<FaChevronLeft size={12} />}
            style={{
              height: 42,
              borderRadius: 14,
              color: "var(--mantine-color-dimmed)",
            }}
          >
            Back
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.42,
            delay: 0.05,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <Box mt={22}>
            <Text
              size="xs"
              fw={800}
              tt="uppercase"
              c={APP_CLIP_ACCENT}
              style={{ letterSpacing: "0.11em" }}
            >
              Access options
            </Text>

            <Title
              order={1}
              mt={7}
              style={{
                fontSize: "clamp(32px, 8vw, 42px)",
                lineHeight: 1.04,
                letterSpacing: "-0.04em",
                fontWeight: 850,
              }}
            >
              How would you like to enter?
            </Title>

            <Text
              c="dimmed"
              mt={12}
              mb={26}
              style={{
                maxWidth: 510,
                fontSize: 16,
                lineHeight: 1.55,
                fontWeight: 500,
              }}
            >
              {purchasesUnavailable
                ? "New purchases are currently unavailable outside the configured opening hours."
                : canPurchaseSession && canPurchaseMembership
                  ? "Choose a one-time entry for this visit, or a membership for ongoing access."
                  : canPurchaseSession
                    ? "Purchase a one-time entry for this visit."
                    : "Choose a membership for ongoing access."}
            </Text>
          </Box>
        </motion.div>

        <Flex direction="column" gap={16}>
          {canPurchaseSession && (
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.45,
                delay: 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={
                sessionPurchasesOpen
                  ? {
                    y: -4,
                    scale: 1.008,
                    transition: { duration: 0.2 },
                  }
                  : undefined
              }
              whileTap={
                sessionPurchasesOpen
                  ? {
                    scale: 0.992,
                    transition: { duration: 0.08 },
                  }
                  : undefined
              }
            >
              <Box
                style={{
                  ...cardBase,
                  background:
                    "linear-gradient(140deg, rgba(232, 245, 255, 0.94) 0%, rgba(255, 255, 255, 0.78) 58%, rgba(255, 255, 255, 0.62) 100%)",
                  opacity: sessionPurchasesOpen ? 1 : 0.72,
                }}
              >
                <Box
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    right: -65,
                    top: -82,
                    background: "rgba(34, 139, 230, 0.14)",
                    filter: "blur(102px)",
                  }}
                />

                <Flex
                  justify="space-between"
                  align="flex-start"
                  gap={16}
                  style={{ position: "relative", zIndex: 1 }}
                >
                  <Flex
                    gap={14}
                    align="flex-start"
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Center
                      style={{
                        width: 52,
                        height: 52,
                        flexShrink: 0,
                        borderRadius: 17,
                        color: APP_CLIP_ACCENT,
                        background: "rgba(34, 139, 230, 0.12)",
                        border: "1px solid rgba(34, 139, 230, 0.12)",
                      }}
                    >
                      <FaTicketAlt size={21} />
                    </Center>

                    <Box style={{ minWidth: 0 }}>
                      <Text
                        size="xs"
                        fw={800}
                        tt="uppercase"
                        c={APP_CLIP_ACCENT}
                        style={{ letterSpacing: "0.08em" }}
                      >
                        One-time access
                      </Text>

                      <Text
                        fw={850}
                        mt={3}
                        style={{
                          fontSize: 22,
                          lineHeight: 1.15,
                          letterSpacing: "-0.025em",
                        }}
                      >
                        Get Entry
                      </Text>
                    </Box>
                  </Flex>
                </Flex>

                {sessionPurchasesOpen &&
                  endTillCloseTime &&
                  Number(
                    sessionPurchaseAvailability?.currentSlotRemainingMinutes,
                  ) > 0 && (
                    <Box
                      mt={14}
                      style={{
                        position: "relative",
                        zIndex: 1,
                        padding: "11px 13px",
                        borderRadius: 14,
                        background: "rgba(34, 139, 230, 0.075)",
                      }}
                    >
                      <Text size="sm" fw={650} c="dimmed">
                        Access is available until{" "}
                        <Text component="span" fw={800} c="dark">
                          {sessionPurchaseAvailability.currentSlotCloseTime}
                        </Text>{" "}
                        today.
                      </Text>
                    </Box>
                  )}

                <motion.div
                  whileHover={sessionPurchasesOpen ? { x: 2 } : undefined}
                  transition={{ duration: 0.18 }}
                >
                  <Button
                    fullWidth
                    mt={20}
                    onClick={onPurchaseSession}
                    disabled={!sessionPurchasesOpen}
                    rightSection={
                      sessionPurchasesOpen ? <FaArrowRight size={12} /> : null
                    }
                    style={{
                      ...appClipStyles.prominentButton,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {sessionPurchasesOpen
                      ? `Purchase - ${perPersonPriceLabel}`
                      : "Currently unavailable"}
                  </Button>
                </motion.div>

                {!sessionPurchasesOpen && sessionPurchaseAvailability && (
                  <Flex
                    gap={8}
                    wrap="wrap"
                    mt={12}
                    style={{ position: "relative", zIndex: 1 }}
                  >
                    <Text size="xs" c="dimmed" fw={600}>
                      Today: {sessionPurchaseAvailability.todayHoursLabel}
                    </Text>

                    {sessionPurchaseAvailability.nextOpeningLabel && (
                      <Text size="xs" c="dimmed" fw={600}>
                        • Reopens {sessionPurchaseAvailability.nextOpeningLabel}
                      </Text>
                    )}
                  </Flex>
                )}
              </Box>
            </motion.div>
          )}

          {canPurchaseMembership && (
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 0.45,
                delay: canPurchaseSession ? 0.2 : 0.12,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={
                sessionPurchasesOpen
                  ? {
                    y: -4,
                    scale: 1.008,
                    transition: { duration: 0.2 },
                  }
                  : undefined
              }
              whileTap={
                sessionPurchasesOpen
                  ? {
                    scale: 0.992,
                    transition: { duration: 0.08 },
                  }
                  : undefined
              }
            >
              <Box
                style={{
                  ...cardBase,
                  background:
                    "linear-gradient(140deg, rgba(243, 240, 255, 0.95) 0%, rgba(255, 255, 255, 0.79) 58%, rgba(255, 255, 255, 0.62) 100%)",
                  opacity: sessionPurchasesOpen ? 1 : 0.72,
                }}
              >
                <Box
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    width: 170,
                    height: 170,
                    borderRadius: "50%",
                    right: -65,
                    top: -82,
                    background: "rgba(121, 80, 242, 0.13)",
                    filter: "blur(102px)",
                  }}
                />

                <Flex
                  gap={14}
                  align="flex-start"
                  style={{ position: "relative", zIndex: 1 }}
                >
                  <Center
                    style={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      borderRadius: 17,
                      color: "#7950f2",
                      background: "rgba(121, 80, 242, 0.11)",
                      border: "1px solid rgba(121, 80, 242, 0.11)",
                    }}
                  >
                    <FaIdCard size={22} />
                  </Center>

                  <Box>
                    <Text
                      size="xs"
                      fw={800}
                      tt="uppercase"
                      style={{
                        color: "#7950f2",
                        letterSpacing: "0.08em",
                      }}
                    >
                      Ongoing access
                    </Text>

                    <Text
                      fw={850}
                      mt={3}
                      style={{
                        fontSize: 22,
                        lineHeight: 1.15,
                        letterSpacing: "-0.025em",
                      }}
                    >
                      Membership
                    </Text>

                    {/* <Text c="dimmed" size="sm" mt={6} style={{ lineHeight: 1.45 }}>
                      Choose from the available membership options for repeat
                      visits and ongoing access.
                    </Text> */}
                  </Box>
                </Flex>

                <Box
                  mt={18}
                  style={{
                    position: "relative",
                    zIndex: 1,
                    padding: "12px 14px",
                    borderRadius: 15,
                    background: "rgba(121, 80, 242, 0.065)",
                  }}
                >
                  <Text size="sm" fw={700} style={{ color: "#5f3dc4" }}>
                    See membership options, pricing and duration
                  </Text>
                </Box>

                <motion.div
                  whileHover={sessionPurchasesOpen ? { x: 2 } : undefined}
                  transition={{ duration: 0.18 }}
                >
                  <Button
                    fullWidth
                    mt={20}
                    variant="light"
                    color="violet"
                    onClick={onPurchaseMembership}
                    disabled={!sessionPurchasesOpen}
                    rightSection={
                      sessionPurchasesOpen ? <FaArrowRight size={12} /> : null
                    }
                    style={{
                      ...appClipStyles.prominentButton,
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    {sessionPurchasesOpen
                      ? "Browse memberships"
                      : "Currently unavailable"}
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          )}
        </Flex>

        {!canPurchaseSession && !canPurchaseMembership && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.12 }}
          >
            <Box style={appClipStyles.actionCard}>
              <Text fw={700}>No purchase options are currently available.</Text>
            </Box>
          </motion.div>
        )}

        {errorMsg && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Text c="red" size="sm" fw={600} mt="md">
              {errorMsg}
            </Text>
          </motion.div>
        )}
      </Box>
    </Box>
  );
});

const MembershipOnlyView = React.memo(function MembershipOnlyView({
  title = "Members-only access",
  message = "This facility can only be accessed by member users. Please download our full app to access this.",
  appleLink = "",
  googleLink = "",
  logoAnimation = null,
  isLogoAnimationLoading = false,
}) {
  const hasApple = typeof appleLink === "string" && appleLink.trim().length > 0;
  const hasGoogle =
    typeof googleLink === "string" && googleLink.trim().length > 0;

  return (
    <AppClipStatusScaffold>
      <Flex direction="column" align="center" gap={18}>
        <Center
          style={{
            width: 148,
            height: 148,
            borderRadius: "50%",
            ...appClipStyles.glassCircle,
          }}
        >
          <Lottie
            animationData={isLogoAnimationLoading? imagePlaceholder: logoAnimation}
            loop={isLogoAnimationLoading}
            autoplay
            style={{ width: 112, height: 112 }}
          />
        </Center>

        <Title
          order={2}
          ta="center"
          style={{
            fontSize: 30,
            lineHeight: 1.1,
            letterSpacing: "-0.025em",
          }}
        >
          {title}
        </Title>

        <Text c="dimmed" fw={500} ta="center" style={{ maxWidth: 380 }}>
          {message}
        </Text>

        <Flex gap="md" wrap="wrap" justify="center" w="100%" mt={4}>
          {hasApple && (
            <AppStoreButton
              url={appleLink}
              theme="dark"
              className="custom-style"
              width="auto"
            />
          )}

          {hasGoogle && (
            <GooglePlayButton
              url={googleLink}
              theme="dark"
              className="custom-style"
              width="auto"
            />
          )}
        </Flex>
      </Flex>
    </AppClipStatusScaffold>
  );
});

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
export default function SmartLockPage() {
  const { siteID, itemID } = useParams();

  const [authFlow, setAuthFlow] = useState(AuthFlow.UNKNOWN);

  const [name, setName] = useState("");

  const [phoneNumber, setPhoneNumber] = useState("");

  const [errorMsg, setErrorMsg] = useState("");

  const [verificationCodeErr, setVerificationCodeErr] = useState("");

  const [remainingTime, setRemainingTime] = useState(300);

  const [timerId, setTimerId] = useState(null);

  const [showConfetti, setShowConfetti] = useState(false);

  const [site, setSite] = useState({});

  const [sessionOpeningHours, setSessionOpeningHours] = useState({
    weekdays: [],
    specificDates: [],
  });

  const [sessionOpeningHoursTick, setSessionOpeningHoursTick] = useState(0);

  const [lock, setLock] = useState({});

  const [invalidLock, setInvalidLock] = useState(false);

  const [isBanned, setIsBanned] = useState(false);

  const [isOutage, setIsOutage] = useState(false);

  const [logoAnimation, setLogoAnimation] = useState(null);

  const [isLogoAnimationLoading, setIsLogoAnimationLoading] = useState(true);

  const [appStoreLink, setAppStoreLink] = useState("");

  const [googlePlayStoreLink, setGooglePlayStoreLink] = useState("");

  const [isMembershipOnly, setIsMembershipOnly] = useState(false);

  const [availableMemberships, setAvailableMemberships] = useState([]);

  const [purchasingMembershipId, setPurchasingMembershipId] = useState("");

  const [membershipPaymentSession, setMembershipPaymentSession] =
    useState(null);

  const [showReportDamage, setShowReportDamage] = useState(false);

  const [hasReportDamage, setHasReportDamage] = useState(false);

  const [sessionId, setSessionId] = useState(null);

  const [customerJwtToken, setCustomerJwtToken] = useState(() => {
    try {
      return localStorage.getItem("customer_session_token");
    } catch {
      return null;
    }
  });

  const [sessionLength, setSessionLength] = useState(0);

  const [isHireStarted, setIsHireStarted] = useState(false);

  const [startingHire, setStartingHire] = useState(false);

  const [unlocking, setUnlocking] = useState(false);

  const [hireMode, setHireMode] = useState("initial");

  const [supportPhone, setSupportPhone] = useState(null);

  const [businessTitle, setBusinessTitle] = useState("");

  const [businessId, setBusinessId] = useState(null);

  const [stripePublishableKey, setStripePublishableKey] = useState(null);

  const [browserInfo] = useState(() => detectBrowserInfo());

  const [geoPermissionState, setGeoPermissionState] = useState("unknown");

  const [locationErrorCode, setLocationErrorCode] = useState(null);

  const isCareZenBusiness = Number(businessId) === 20;

  const shouldUseDotNetCustomerFlow = ((Number(businessId) === 20 || Number(businessId) === 56 || Number(businessId) === 22 || Number(businessId) === 23 || Number(businessId) === 27) && !lock?.is_ajax_item);

  const sessionPurchaseAvailability = useMemo(
    () =>
      getSessionPurchaseAvailability({
        weekdays: sessionOpeningHours.weekdays,
        specificDates: sessionOpeningHours.specificDates,
        siteTimezone: site?.timezone,
      }),
    [
      sessionOpeningHours.weekdays,
      sessionOpeningHours.specificDates,
      site?.timezone,
      sessionOpeningHoursTick,
    ],
  );

  function getMembershipAccessMinutes() {
    const fallbackMinutes = Math.max(
      0,
      Math.floor(toNumber(lock?.hire_max_length, 5)),
    );

    if (!truthy(lock?.end_til_close_time)) {
      return fallbackMinutes;
    }

    const liveAvailability = getSessionPurchaseAvailability({
      weekdays: sessionOpeningHours.weekdays,
      specificDates: sessionOpeningHours.specificDates,
      siteTimezone: site?.timezone,
    });

    setSessionOpeningHoursTick(Date.now());

    // If end_til_close_time is enabled but no schedule exists,
    // retain the configured max length rather than producing 0 minutes.
    if (!liveAvailability.hasSchedule) {
      return fallbackMinutes;
    }

    return Math.max(
      0,
      Math.floor(toNumber(liveAvailability.currentSlotRemainingMinutes, 0)),
    );
  }

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSessionOpeningHoursTick(Date.now());
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const isPurchaseFlow =
      authFlow === AuthFlow.ACCESS_PURCHASE_CHOICE ||
      authFlow === AuthFlow.MEMBERSHIP_PURCHASE;

    if (
      isPurchaseFlow &&
      sessionPurchaseAvailability.hasSchedule &&
      !sessionPurchaseAvailability.isOpen
    ) {
      setMembershipPaymentSession(null);
      setPurchasingMembershipId("");
      setErrorMsg("");
      setAuthFlow(AuthFlow.ACCESS_PURCHASE_CLOSED);
    }
  }, [
    authFlow,
    sessionPurchaseAvailability.hasSchedule,
    sessionPurchaseAvailability.isOpen,
  ]);

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 1800);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [showConfetti]);

  useEffect(() => {
    if (!itemID) {
      setAuthFlow(AuthFlow.NO_LOCK);

      return;
    }

    setLogoAnimation(null);

    setIsLogoAnimationLoading(true);

    getSiteData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemID]);

  useEffect(() => {
    if (Object.values(site).length > 0) {
      requestLocationCheck();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  function startCountdown() {
    if (timerId) {
      clearInterval(timerId);
    }

    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);

          setAuthFlow(AuthFlow.EXPIRED);

          onEndHire();

          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    setTimerId(id);
  }

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED && !lock?.is_ajax_item) {
      startCountdown();

      return () => {
        if (timerId) {
          clearInterval(timerId);
        }
      };
    }

    return undefined;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item]);

  useEffect(() => {
    if (
      authFlow === AuthFlow.UNLOCKED &&
      lock?.is_ajax_item &&
      isHireStarted &&
      !timerId
    ) {
      startCountdown();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item, isHireStarted]);

  useEffect(() => {
    if (authFlow !== AuthFlow.UNLOCKED && timerId) {
      clearInterval(timerId);

      setTimerId(null);
    }
  }, [authFlow, timerId]);

  async function syncGeolocationPermission() {
    try {
      if (navigator.permissions?.query) {
        const perm = await navigator.permissions.query({
          name: "geolocation",
        });

        setGeoPermissionState(perm.state || "unknown");

        return perm.state || "unknown";
      }
    } catch {
      // ignore
    }

    return "unknown";
  }

  function applyLoadedSitePayload(data = {}, fallbackBusinessId = 0) {
    const membershipItem = truthy(data.is_membership);

    setIsMembershipOnly(membershipItem);
    setGooglePlayStoreLink(data?.google_link ?? "");
    setAppStoreLink(data?.apple_link ?? data?.apple ?? "");

    const nextSite = data.site || {};

    const nextItem = data.item || {};

    if (!Object.keys(nextSite).length || !Object.keys(nextItem).length) {
      setHasReportDamage(false);

      setAuthFlow(AuthFlow.NO_LOCK);

      setErrorMsg("Could not load venue.");

      setInvalidLock(true);

      setLogoAnimation(null);

      return;
    }

    const normalisedItem = {
      ...nextItem,
      is_ajax_item: truthy(nextItem.is_ajax_item),
      is_outage: truthy(nextItem.is_outage),
      hire_length: toNumber(
        nextItem.hire_length ?? nextItem.min_hire_length,
        0,
      ),
      hire_max_length: toNumber(
        nextItem.hire_max_length ?? nextItem.max_hire_length,
        0,
      ),
      hire_cost_base: toNumber(nextItem.hire_cost_base ?? nextItem.cost, 0),
      hire_cost_base_person: toNumber(nextItem.hire_cost_base_person, 0),
      charge_per_session: truthy(nextItem.charge_per_session),
      end_til_close_time: truthy(nextItem.end_til_close_time),
    };

    const outage = truthy(normalisedItem.is_outage);

    setIsOutage(outage);

    if (outage) {
      setHasReportDamage(false);

      setSite({});

      setLock({});

      setLogoAnimation(null);

      return;
    }

    const nextBusinessId = toNumber(
      data.business_id ??
      data.businessId ??
      nextSite.business_id ??
      nextSite.businessId ??
      nextItem.business_id ??
      nextItem.businessId,
      fallbackBusinessId,
    );

    if (nextBusinessId > 0) {
      setBusinessId(nextBusinessId);
    }

    const nextStripeKey =
      data.stripe_key ||
      data.stripe_public_key ||
      data.stripePublishableKey ||
      "";

    if (nextStripeKey) {
      setStripePublishableKey(nextStripeKey);
    }

    setHasReportDamage(
      truthy(
        data.has_damage_report ??
        data.hasReportDamage ??
        data.has_report_damage,
      ),
    );

    setSessionOpeningHours({
      weekdays: data.weekdays ?? data?.timeslots?.weekdays ?? [],
      specificDates:
        data.specific_dates ?? data?.timeslots?.specific_dates ?? [],
    });

    setSessionOpeningHoursTick(Date.now());

    setSite(nextSite);

    setLock(normalisedItem);

    setLogoAnimation(data.logo_animation || null);

    setSupportPhone(data.support_phone || null);

    setBusinessTitle(data.business_title || "");

    setInvalidLock(false);
  }

  async function getSiteData() {
    setIsLogoAnimationLoading(true);
    try {
      const res = await DotNetAPI.get(`sites/${siteID}/items/${itemID}/app`);

      const response = res?.data || {};

      console.log(response, "lolololololololol");

      if (
        Object.prototype.hasOwnProperty.call(response, "status") &&
        !apiStatusIsSuccess(response)
      ) {
        setAuthFlow(AuthFlow.NO_LOCK);

        setErrorMsg(getApiMessage(response, "Could not load venue."));

        setInvalidLock(true);

        setLogoAnimation(null);

        setHasReportDamage(false);

        return;
      }

      const payload = response?.data || response;

      if (!payload || typeof payload !== "object") {
        setAuthFlow(AuthFlow.NO_LOCK);

        setErrorMsg("Could not load venue.");

        setInvalidLock(true);

        setLogoAnimation(null);

        setHasReportDamage(false);

        return;
      }

      const nextBusinessId = toNumber(
        payload.business_id ??
        payload.businessId ??
        payload.site?.business_id ??
        payload.site?.businessId ??
        payload.item?.business_id ??
        payload.item?.businessId,
        0,
      );

      if (nextBusinessId > 0) {
        setBusinessId(nextBusinessId);
      }

      applyLoadedSitePayload(payload, nextBusinessId);
    } catch (err) {

      console.log('yoyoyoyoyoyoyo')
      setAuthFlow(AuthFlow.NO_LOCK);

      setErrorMsg(
        err?.response?.data?.message || err?.message || "Could not load venue.",
      );

      setInvalidLock(true);

      setLogoAnimation(null);

      setHasReportDamage(false);
    } finally {
      setIsLogoAnimationLoading(false);
    }
  }

  async function requestLocationCheck() {
    if (CONFIG.devMode || isCareZenBusiness) {
      setLocationErrorCode(null);

      setGeoPermissionState("granted");

      setErrorMsg("");

      setAuthFlow(AuthFlow.INPUT_DETAILS);

      return true;
    }

    const lat = Number(site?.lat);

    const lng = Number(site?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      console.warn("[Location] Missing site lat/lng");

      setLocationErrorCode(null);

      setErrorMsg("This site does not have a valid location configured.");

      setAuthFlow(AuthFlow.NO_LOCATION);

      return false;
    }

    await syncGeolocationPermission();

    try {
      const { pos, coarse } = await getBestEffortPosition();

      const coords = pos.coords || {};

      const user = {
        lat: coords.latitude,
        lng: coords.longitude,
      };

      const target = {
        lat,
        lng,
      };

      const { ok } = isWithinAllowedRadius({
        user,
        target,
        accuracy: coords.accuracy,
        coarse,
      });

      setLocationErrorCode(null);

      setGeoPermissionState("granted");

      if (ok) {
        setErrorMsg("");

        setAuthFlow(AuthFlow.INPUT_DETAILS);

        return true;
      }

      setErrorMsg("");

      setAuthFlow(AuthFlow.NOT_NEAR_LOCATION);

      return false;
    } catch (err) {
      console.warn("[Location] best-effort failed:", err);

      const latestPermissionState = await syncGeolocationPermission();

      setLocationErrorCode(err?.code ?? null);

      if (err?.code === 1 || latestPermissionState === "denied") {
        setErrorMsg(
          "Location access was denied. Please change this browser’s location permission to Ask or Allow, then tap Try again.",
        );
      } else if (err?.code === 2) {
        setErrorMsg(
          "We could not determine your location. Please make sure Location Services are enabled on your device, then try again.",
        );
      } else if (err?.code === 3) {
        setErrorMsg(
          "Location request timed out. Please move to an area with better signal and try again.",
        );
      } else {
        setErrorMsg(
          "We could not get your location. Please check your device and browser location settings, then try again.",
        );
      }

      setAuthFlow(AuthFlow.NO_LOCATION);

      return false;
    }
  }

  function getAuthBody(twoFa = "") {
    const body = {
      phone: formatPhoneNumber(phoneNumber),
      business_id: businessId,
      name: name.trim() === "" ? "Opt Out" : name.trim(),
      tz: moment.tz.guess(),
    };

    if (twoFa) {
      body.two_fa = String(twoFa).trim();
    }

    return body;
  }

  function getUnlockBody(sessionIdOverride = null) {
    return {
      item_id: Number(itemID),

      name: name.trim() === "" ? "Opt Out" : name.trim(),

      phone: formatPhoneNumber(phoneNumber),

      tz: moment.tz.guess(),

      business_id: businessId,

      hire_length: toNumber(lock?.hire_length, 0),

      session_id: toNumber(sessionIdOverride ?? sessionId, 0),
    };
  }

  function handleBannedUser(user = {}) {
    if (!truthy(user?.is_banned)) {
      return false;
    }

    setAuthFlow(AuthFlow.INPUT_DETAILS);

    if (businessTitle !== "") {
      setErrorMsg(
        `Your account has been banned by ${businessTitle}, please contact ${businessTitle} for more information`,
      );
    } else {
      setErrorMsg(
        "Your account has been banned, please contact support for more information",
      );
    }

    setIsBanned(true);

    return true;
  }

  async function checkHiredSession(tokenOverride = null) {
    const token = tokenOverride || customerJwtToken;

    if (!itemID || !token) {
      return false;
    }

    try {
      const response = await DotNetAPI.post(
        `hireable-items/${itemID}/sessions/check`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const json = response?.data || {};

      if (!apiStatusIsSuccess(json)) {
        console.log(
          "checkHiredSession failed:",
          getApiMessage(json, "Unknown error"),
        );

        return false;
      }

      const data = json?.data || {};

      if (!truthy(data?.has_session) || !data?.session) {
        return false;
      }

      const session = data.session;
      const restoredSessionId = toNumber(session?.id, 0);
      const restoredLength = toNumber(session?.length, 0);
      const restoredRemaining = toNumber(
        session?.remaining_seconds ?? session?.remain_seconds,
        0,
      );
      const restoredStarted = truthy(session?.is_started ?? session?.started);

      if (restoredSessionId <= 0 || restoredLength <= 0) {
        return false;
      }

      setSessionId(restoredSessionId);
      setSessionLength(restoredLength);
      setRemainingTime(restoredRemaining);
      setIsHireStarted(restoredStarted);
      setErrorMsg("");
      setAuthFlow(AuthFlow.UNLOCKED);

      return true;
    } catch (err) {
      console.log(
        "checkHiredSession error:",
        err?.response?.data?.message || err?.message || err?.toString(),
      );

      return false;
    }
  }

  async function unlockCurrentItem(
    tokenOverride = null,
    sessionIdOverride = null,
  ) {
    const token = tokenOverride || customerJwtToken;

    try {
      const res = await DotNetAPI.put(
        `sites/${siteID}/items/${itemID}/customer-unlock`,
        getUnlockBody(sessionIdOverride),
        token
          ? {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
          : undefined,
      );

      const json = res?.data || {};
      const data = json?.data || {};

      if (
        json?.code === "outside_opening_hours" ||
        (!apiStatusIsSuccess(json) &&
          String(json?.message || "")
            .toLowerCase()
            .includes("cannot be unlocked outside"))
      ) {
        setVerificationCodeErr("");
        setErrorMsg("");
        setAuthFlow(AuthFlow.OUTSIDE_OPENING_HOURS);

        const openingHoursError = new Error(
          "This door cannot be unlocked outside the opening hours.",
        );

        openingHoursError.code = "outside_opening_hours";
        openingHoursError.apiData = json;

        throw openingHoursError;
      }

      if (!apiStatusIsSuccess(json)) {
        throw new Error(getApiMessage(json, "Failed to unlock."));
      }

      if (truthy(data.is_hired_by_another)) {
        throw new Error(
          "This facility has been hired by another person. Please try again later.",
        );
      }

      return json;
    } catch (error) {
      if (isOutsideOpeningHoursError(error)) {
        setVerificationCodeErr("");
        setErrorMsg("");
        setAuthFlow(AuthFlow.OUTSIDE_OPENING_HOURS);

        if (error?.code === "outside_opening_hours") {
          throw error;
        }

        const openingHoursError = new Error(
          "This door cannot be unlocked outside the opening hours.",
        );

        openingHoursError.code = "outside_opening_hours";
        openingHoursError.apiData = error?.response?.data || {};

        throw openingHoursError;
      }

      throw error;
    }
  }

  async function loadAccessMemberships(tokenOverride = null) {
    const token = tokenOverride || customerJwtToken;

    if (!businessId || !token) {
      return [];
    }

    const response = await DotNetAPI.get(
      `businesses/${businessId}/app/memberships/access`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const json = response?.data || {};

    if (!apiStatusIsSuccess(json)) {
      throw new Error(getApiMessage(json, "Unable to load memberships."));
    }

    const memberships = json?.data?.memberships;

    return Array.isArray(memberships) ? memberships : [];
  }

  async function continueMembershipAccess(user, tokenOverride = null) {
    const token = tokenOverride || customerJwtToken;

    if (!token) {
      setErrorMsg(
        "Your verified session could not be loaded. Please try again.",
      );
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      return;
    }

    if (customerHasActiveMembership(user)) {
      const accessMinutes = getMembershipAccessMinutes();

      await unlockCurrentItem(token);

      setShowConfetti(true);
      setRemainingTime(accessMinutes * 60);
      setAuthFlow(AuthFlow.UNLOCKED);
      return;
    }

    const restoredSession = await checkHiredSession(token);

    if (restoredSession) {
      return;
    }

    const liveAvailability = getSessionPurchaseAvailability({
      weekdays: sessionOpeningHours.weekdays,
      specificDates: sessionOpeningHours.specificDates,
      siteTimezone: site?.timezone,
    });

    setSessionOpeningHoursTick(Date.now());

    if (!liveAvailability.isOpen) {
      setAvailableMemberships([]);
      setMembershipPaymentSession(null);
      setPurchasingMembershipId("");
      setErrorMsg("");
      setAuthFlow(AuthFlow.ACCESS_PURCHASE_CLOSED);
      return;
    }

    const canPurchaseSession = canPurchaseHireSession(lock);

    try {
      const memberships = await loadAccessMemberships(token);
      const canPurchaseMembership = memberships.length > 0;

      setAvailableMemberships(memberships);
      setMembershipPaymentSession(null);
      setPurchasingMembershipId("");
      setErrorMsg("");

      if (canPurchaseSession && canPurchaseMembership) {
        setAuthFlow(AuthFlow.ACCESS_PURCHASE_CHOICE);
        return;
      }

      if (canPurchaseSession) {
        setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);
        return;
      }

      setAuthFlow(AuthFlow.MEMBERSHIP_PURCHASE);
    } catch (error) {
      setAvailableMemberships([]);

      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to load memberships.";

      if (canPurchaseSession) {
        setErrorMsg("");
        setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);
        return;
      }

      setErrorMsg(message);
      setAuthFlow(AuthFlow.MEMBERSHIP_PURCHASE);
    }
  }

  async function startMembershipPurchase(membership) {
    const liveAvailability = getSessionPurchaseAvailability({
      weekdays: sessionOpeningHours.weekdays,
      specificDates: sessionOpeningHours.specificDates,
      siteTimezone: site?.timezone,
    });

    setSessionOpeningHoursTick(Date.now());

    if (!liveAvailability.isOpen) {
      setMembershipPaymentSession(null);
      setPurchasingMembershipId("");
      setErrorMsg("");
      setAuthFlow(AuthFlow.ACCESS_PURCHASE_CLOSED);
      return;
    }

    const token = customerJwtToken;
    const membershipId = getMembershipId(membership);

    if (!token || !businessId || membershipId <= 0) {
      setErrorMsg("Unable to start this membership purchase.");
      return;
    }

    setErrorMsg("");
    setPurchasingMembershipId(String(membershipId));

    try {
      const response = await DotNetAPI.post(
        `businesses/${businessId}/memberships/${membershipId}/access/payment`,
        {
          is_concession: false,
          discount_id: 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const json = response?.data || {};

      if (!apiStatusIsSuccess(json)) {
        throw new Error(getApiMessage(json, "Unable to start payment."));
      }

      const data = json?.data || {};
      const intentResponse = data?.intent || {};
      const intent = intentResponse?.data || {};
      const clientSecret = intent?.client_secret || "";
      const publishableKey = intent?.business_pk || stripePublishableKey || "";
      const transactionId = toNumber(data?.transaction_id, 0);
      const amountCents = toNumber(
        data?.amount,
        Math.round(getMembershipPriceDollars(membership) * 100),
      );

      if (!clientSecret || !publishableKey || transactionId <= 0) {
        throw new Error("Payment details are incomplete.");
      }

      setMembershipPaymentSession({
        membership,
        membershipId,
        clientSecret,
        publishableKey,
        transactionId,
        amountCents,
      });
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
        error?.message ||
        "Unable to start payment.",
      );
    } finally {
      setPurchasingMembershipId("");
    }
  }

  async function completeMembershipPurchase(purchaseContext = null) {
    const token = customerJwtToken;
    const session = membershipPaymentSession;

    if (!token || !businessId || !session) {
      setErrorMsg("Payment session is unavailable.");
      return false;
    }

    setErrorMsg("");

    try {
      const response = await DotNetAPI.post(
        `businesses/${businessId}/memberships/${session.membershipId}/access/complete-payment`,
        {
          transaction_id: session.transactionId,
          is_concession: false,
          discount_id: 0,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const json = response?.data || {};

      if (!apiStatusIsSuccess(json)) {
        throw new Error(
          getApiMessage(json, "Unable to activate this membership."),
        );
      }

      // Payment + membership activation succeeded.
      // Move away from the membership checkout immediately so clearing the
      // payment session cannot briefly render the membership selection screen
      // while the unlock request is still running.
      setAuthFlow(AuthFlow.LOADING);
      setMembershipPaymentSession(null);

      const accessMinutes = truthy(lock?.end_til_close_time)
        ? Math.max(
          0,
          Math.floor(
            toNumber(
              purchaseContext?.accessMinutes,
              getMembershipAccessMinutes(),
            ),
          ),
        )
        : getMembershipAccessMinutes();

      await unlockCurrentItem(token);

      setShowConfetti(true);
      setRemainingTime(accessMinutes * 60);
      setAuthFlow(AuthFlow.UNLOCKED);

      return true;
    } catch (error) {
      if (isOutsideOpeningHoursError(error)) {
        return false;
      }

      setErrorMsg(
        error?.response?.data?.message ||
        error?.message ||
        "Payment succeeded but the membership could not be activated.",
      );

      setAuthFlow(AuthFlow.MEMBERSHIP_PURCHASE);

      return false;
    }
  }

  async function handleUnlockClick() {
    if (!phoneNumber) {
      setErrorMsg("Phone number is required.");

      return;
    }

    if (!businessId) {
      setErrorMsg("Still loading venue. Please wait a second and try again.");

      return;
    }

    setErrorMsg("");

    setVerificationCodeErr("");

    setAuthFlow(AuthFlow.LOADING);

    const submittedName = name.trim() === "" ? "Opt Out" : name;

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const response = await legacyCheckUserAuth(
          itemID,
          submittedName,
          phoneNumber,
          "",
          businessId,
        );

        const data = response?.data || {};

        if (truthy(data.is_banned)) {
          handleBannedUser({
            is_banned: true,
          });

          return;
        }

        if (truthy(data.is_hired_by_another)) {
          setAuthFlow(AuthFlow.INPUT_DETAILS);

          setErrorMsg(
            "This facility has been hired by another person. Please try again later.",
          );

          return;
        }

        if (lock?.is_ajax_item && truthy(data.has_session) && data.session) {
          const sess = data.session;

          setSessionId(sess.id);

          setSessionLength(Number(sess.length || 0));

          const remainSec = Number(
            sess.remaining_seconds ?? sess.remain_seconds ?? 0,
          );

          const started = truthy(sess.is_started) || truthy(sess.started);

          if (started) {
            setIsHireStarted(true);

            setRemainingTime(remainSec);
          } else {
            setIsHireStarted(false);

            setRemainingTime(Number(sess.length || 0) * 60);
          }

          setAuthFlow(AuthFlow.UNLOCKED);

          return;
        }

        if (truthy(data.auth)) {
          if (lock?.is_ajax_item) {
            setHireMode("initial");

            setAuthFlow(AuthFlow.HIRE_SELECT);
          } else {
            setShowConfetti(true);

            setAuthFlow(AuthFlow.UNLOCKED);
          }
        } else {
          setAuthFlow(AuthFlow.ENTER_OTP);
        }

        return;
      }

      const res = await DotNetAPI.post("auth/customer", getAuthBody());

      const json = res?.data || {};

      if (!apiStatusIsSuccess(json)) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);

        setErrorMsg(getApiMessage(json, "Failed to send verification code."));

        return;
      }

      const data = json?.data || {};

      console.log(data, "bruhrurhuh");

      if (handleBannedUser(data?.user)) {
        return;
      }

      if (truthy(data.auth)) {
        const sessionToken =
          typeof data?.session_token === "string"
            ? data.session_token.trim()
            : "";

        if (!sessionToken) {
          setAuthFlow(AuthFlow.INPUT_DETAILS);
          setErrorMsg(
            "Authentication succeeded but no session token was returned.",
          );
          return;
        }

        try {
          localStorage.setItem("customer_session_token", sessionToken);
        } catch {
          // If localStorage is unavailable, the token still remains in React state.
        }

        setCustomerJwtToken(sessionToken);

        if (isMembershipOnly) {
          await continueMembershipAccess(data?.user || {}, sessionToken);
          return;
        }

        const restoredSession = await checkHiredSession(sessionToken);

        if (restoredSession) {
          return;
        }

        if (!lock?.is_ajax_item && canPurchaseHireSession(lock)) {
          setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);
          return;
        }

        if (lock?.is_ajax_item) {
          setHireMode("initial");
          setAuthFlow(AuthFlow.HIRE_SELECT);
          return;
        }

        await unlockCurrentItem(sessionToken);

        setShowConfetti(true);
        setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);
        setAuthFlow(AuthFlow.UNLOCKED);
        return;
      }

      setAuthFlow(AuthFlow.ENTER_OTP);
    } catch (err) {
      if (isOutsideOpeningHoursError(err)) {
        return;
      }

      setAuthFlow(AuthFlow.INPUT_DETAILS);

      setErrorMsg(
        `Failed to authenticate: ${err?.response?.data?.message || err.message || err.toString()
        }`,
      );
    }
  }

  async function handleConfirmOTP(codeEnteredByUser) {
    if (!codeEnteredByUser) {
      setVerificationCodeErr("Verification code is required.");

      setAuthFlow(AuthFlow.ENTER_OTP);

      return;
    }

    if (!businessId) {
      setVerificationCodeErr(
        "Still loading venue. Please wait a second and try again.",
      );

      setAuthFlow(AuthFlow.ENTER_OTP);

      return;
    }

    setVerificationCodeErr("");

    setAuthFlow(AuthFlow.UNLOCKING);

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const compareRes = await legacyConfirmVerificationCode(
          itemID,
          name,
          phoneNumber,
          codeEnteredByUser,
          businessId,
        );

        const ok = truthy(compareRes?.data?.auth);

        if (!ok) {
          setVerificationCodeErr("Invalid verification code.");

          setAuthFlow(AuthFlow.ENTER_OTP);

          return;
        }

        setShowConfetti(true);

        if (lock?.is_ajax_item) {
          setHireMode("initial");

          setAuthFlow(AuthFlow.HIRE_SELECT);
        } else {
          setAuthFlow(AuthFlow.UNLOCKED);
        }

        return;
      }

      const verifyRes = await DotNetAPI.post(
        "auth/customer/verify",
        getAuthBody(codeEnteredByUser),
      );

      const verifyJson = verifyRes?.data || {};
      const verifyData = verifyJson?.data || {};

      if (!apiStatusIsSuccess(verifyJson) || !truthy(verifyData.correct)) {
        setVerificationCodeErr(
          getApiMessage(verifyJson, "Invalid verification code."),
        );

        setAuthFlow(AuthFlow.ENTER_OTP);

        return;
      }

      if (handleBannedUser(verifyData?.user)) {
        return;
      }

      const sessionToken =
        typeof verifyData?.session_token === "string"
          ? verifyData.session_token.trim()
          : "";

      if (!sessionToken) {
        setVerificationCodeErr(
          "Authentication succeeded but no session token was returned.",
        );

        setAuthFlow(AuthFlow.ENTER_OTP);

        return;
      }

      try {
        localStorage.setItem("customer_session_token", sessionToken);
      } catch {
        // If localStorage is unavailable, the token still remains in React state.
      }

      setCustomerJwtToken(sessionToken);

      if (isMembershipOnly) {
        await continueMembershipAccess(verifyData?.user || {}, sessionToken);
        return;
      }

      // Only check for an existing session after successful 2FA.
      const restoredSession = await checkHiredSession(sessionToken);

      if (restoredSession) {
        return;
      }

      if (!lock?.is_ajax_item && canPurchaseHireSession(lock)) {
        setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);

        return;
      }

      await unlockCurrentItem(sessionToken);

      setShowConfetti(true);

      setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);

      setAuthFlow(AuthFlow.UNLOCKED);
    } catch (err) {
      if (isOutsideOpeningHoursError(err)) {
        return;
      }

      setVerificationCodeErr(
        `Verification error: ${err?.response?.data?.message || err.message || err.toString()
        }`,
      );

      setAuthFlow(AuthFlow.ENTER_OTP);
    }
  }

  async function handleLockSessionPaymentSuccess({
    transactionId,
    sessionId: createdSessionId,
    length,
    minutes,
  }) {
    setErrorMsg("");
    setAuthFlow(AuthFlow.LOADING);

    try {
      const fallbackLength = toNumber(
        length ?? minutes ?? lock?.hire_max_length,
        0,
      );

      if (createdSessionId) {
        setSessionId(createdSessionId);
      }

      setSessionLength(fallbackLength);
      setRemainingTime(fallbackLength * 60);
      setIsHireStarted(true);

      await unlockCurrentItem(null, createdSessionId);

      setShowConfetti(true);
      setAuthFlow(AuthFlow.UNLOCKED);

      console.log("Lock session payment complete", {
        transactionId,
        sessionId: createdSessionId,
        length,
        minutes,
      });
    } catch (err) {
      if (isOutsideOpeningHoursError(err)) {
        return;
      }

      setErrorMsg(
        err?.response?.data?.message ||
        err?.message ||
        "Payment succeeded but the lock could not be unlocked.",
      );

      setAuthFlow(AuthFlow.UNLOCKED);
    }
  }

  function handleSessionReady({ sessionId: sid, minutes, length }) {
    setSessionId(sid);

    setSessionLength(length || minutes || 0);

    setIsHireStarted(false);

    setRemainingTime((length || minutes || 0) * 60);

    setAuthFlow(AuthFlow.UNLOCKED);
  }

  async function onStartHire() {
    if (!sessionId) {
      setErrorMsg("No session available to start.");

      return;
    }

    setStartingHire(true);

    setErrorMsg("");

    try {
      const res = await activateHiredSession({
        itemID,
        phone: phoneNumber,
        sessionId,
        businessID: businessId,
      });

      const ok = truthy(res?.data?.is_started) || truthy(res?.data?.started);

      if (ok) {
        setIsHireStarted(true);

        if (!timerId) {
          startCountdown();
        }
      } else {
        setErrorMsg("Failed to start hire, please try again.");
      }
    } catch (e) {
      setErrorMsg(e?.message || "Failed to start hire.");
    } finally {
      setStartingHire(false);
    }
  }

  async function onEndHire() {
    if (!sessionId) {
      setErrorMsg("No session available to start.");

      return;
    }

    try {
      await deactivateHiredSession({
        itemID,
        sessionId,
      });
    } catch (e) {
      setErrorMsg(e?.message || "Failed to end hire.");
    } finally {
      setStartingHire(false);
    }
  }

  async function onUnlockAgain() {
    setUnlocking(true);

    setErrorMsg("");

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const unlockRes = await legacyUnlockAgain(
          itemID,
          name,
          phoneNumber,
          "",
          businessId,
        );

        if (!unlockRes?.status) {
          setErrorMsg("Could not unlock again. Please try again.");
        }

        return;
      }

      await unlockCurrentItem();

      // setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);
    } catch (err) {
      if (isOutsideOpeningHoursError(err)) {
        return;
      }

      setErrorMsg(
        `Unlock failed: ${err?.response?.data?.message || err.message || err.toString()
        }`,
      );
    } finally {
      setUnlocking(false);
    }
  }

  if (isOutage) {
    return (
      <Container
        size={700}
        px="md"
        style={{
          height: "100vh",
        }}
      >
        <Center style={S.fullHeightCenter}>
          <FaTools size={100} />

          <Text size="lg" fw={600} mt="md" ta="center">
            This item is currently unavailable.
          </Text>
        </Center>
      </Container>
    );
  }

  if (authFlow === AuthFlow.UNKNOWN) {
    return <LoadingView />;
  }

  if (authFlow === AuthFlow.LOADING) {
    return <LoadingView />;
  }

  if (authFlow === AuthFlow.NO_LOCK) {
  return (
    <NoLockView
      message={
        errorMsg ||
        (invalidLock ? "This item is unavailable." : undefined)
      }
    />
  );
}

  if (!shouldUseDotNetCustomerFlow) {
    return (
      <MembershipOnlyView
        appleLink={appStoreLink}
        googleLink={googlePlayStoreLink}
        logoAnimation={logoAnimation}
        isLogoAnimationLoading={isLogoAnimationLoading}
      />
    );
  }

  if (authFlow === AuthFlow.UNLOCKING) {
    return <UnlockingView />;
  }

  if (authFlow === AuthFlow.NO_LOCATION) {
    return (
      <NoLocationView
        onRetry={requestLocationCheck}
        browserInfo={browserInfo}
        permissionState={geoPermissionState}
        locationErrorCode={locationErrorCode}
      />
    );
  }

  if (authFlow === AuthFlow.NOT_NEAR_LOCATION) {
    return <NotNearLocationView />;
  }

  if (authFlow === AuthFlow.EXPIRED) {
    return <ExpiredView />;
  }

  if (authFlow === AuthFlow.OUTSIDE_OPENING_HOURS) {
    return (
      <OutsideOpeningHoursView
        availability={sessionPurchaseAvailability}
        site={site}
        supportPhone={supportPhone}
      />
    );
  }

  if (authFlow === AuthFlow.ENTER_OTP) {
    return (
      <EnterOtpView
        onConfirmOTP={handleConfirmOTP}
        verificationCodeErr={verificationCodeErr}
        phoneNumber={phoneNumber}
        onBack={() => {
          setVerificationCodeErr("");
          setAuthFlow(AuthFlow.INPUT_DETAILS);
        }}
      />
    );
  }

  if (authFlow === AuthFlow.ACCESS_PURCHASE_CLOSED) {
    return (
      <AccessPurchaseClosedView
        availability={sessionPurchaseAvailability}
        site={site}
        supportPhone={supportPhone}
        onCheckAgain={() => {
          const liveAvailability = getSessionPurchaseAvailability({
            weekdays: sessionOpeningHours.weekdays,
            specificDates: sessionOpeningHours.specificDates,
            siteTimezone: site?.timezone,
          });

          setSessionOpeningHoursTick(Date.now());

          if (liveAvailability.isOpen) {
            continueMembershipAccess(
              {
                membership_id: 0,
              },
              customerJwtToken,
            );
          }
        }}
        onBack={() => {
          setErrorMsg("");
          setAuthFlow(AuthFlow.INPUT_DETAILS);
        }}
      />
    );
  }

  if (authFlow === AuthFlow.ACCESS_PURCHASE_CHOICE) {
    const canPurchaseSession = canPurchaseHireSession(lock);
    const canPurchaseMembership = availableMemberships.length > 0;

    return (
      <AccessPurchaseChoiceView
        canPurchaseSession={canPurchaseSession}
        canPurchaseMembership={canPurchaseMembership}
        sessionCost={lock?.hire_cost_base}
        customerCost={lock?.hire_cost_base_person}
        chargePerSession={truthy(lock?.charge_per_session)}
        endTillCloseTime={truthy(lock?.end_til_close_time)}
        sessionPurchasesOpen={sessionPurchaseAvailability.isOpen}
        sessionPurchaseAvailability={sessionPurchaseAvailability}
        onPurchaseSession={() => {
          const liveAvailability = getSessionPurchaseAvailability({
            weekdays: sessionOpeningHours.weekdays,
            specificDates: sessionOpeningHours.specificDates,
            siteTimezone: site?.timezone,
          });

          setSessionOpeningHoursTick(Date.now());

          if (!liveAvailability.isOpen) {
            return;
          }

          setErrorMsg("");
          setAuthFlow(AuthFlow.LOCK_SESSION_HIRE);
        }}
        onPurchaseMembership={() => {
          const liveAvailability = getSessionPurchaseAvailability({
            weekdays: sessionOpeningHours.weekdays,
            specificDates: sessionOpeningHours.specificDates,
            siteTimezone: site?.timezone,
          });

          setSessionOpeningHoursTick(Date.now());

          if (!liveAvailability.isOpen) {
            setErrorMsg("");
            setAuthFlow(AuthFlow.ACCESS_PURCHASE_CLOSED);
            return;
          }

          setErrorMsg("");
          setAuthFlow(AuthFlow.MEMBERSHIP_PURCHASE);
        }}
        onBack={() => {
          setErrorMsg("");
          setAuthFlow(AuthFlow.INPUT_DETAILS);
        }}
        errorMsg={errorMsg}
      />
    );
  }

  if (authFlow === AuthFlow.MEMBERSHIP_PURCHASE) {
    if (membershipPaymentSession) {
      return (
        <MembershipPaymentView
          session={membershipPaymentSession}
          purchaseOpen={sessionPurchaseAvailability.isOpen}
          onBeforePayment={() => {
            const liveAvailability = getSessionPurchaseAvailability({
              weekdays: sessionOpeningHours.weekdays,
              specificDates: sessionOpeningHours.specificDates,
              siteTimezone: site?.timezone,
            });

            setSessionOpeningHoursTick(Date.now());

            if (!liveAvailability.isOpen) {
              setMembershipPaymentSession(null);
              setPurchasingMembershipId("");
              setErrorMsg("");
              setAuthFlow(AuthFlow.ACCESS_PURCHASE_CLOSED);
              return false;
            }

            const accessMinutes = truthy(lock?.end_til_close_time)
              ? Math.max(
                0,
                Math.floor(
                  toNumber(liveAvailability.currentSlotRemainingMinutes, 0),
                ),
              )
              : Math.max(0, Math.floor(toNumber(lock?.hire_max_length, 5)));

            if (
              truthy(lock?.end_til_close_time) &&
              liveAvailability.hasSchedule &&
              accessMinutes <= 0
            ) {
              setMembershipPaymentSession(null);
              setPurchasingMembershipId("");
              setErrorMsg("");
              setAuthFlow(AuthFlow.ACCESS_PURCHASE_CLOSED);
              return false;
            }

            return {
              accessMinutes,
              closeTime: liveAvailability.currentSlotCloseTime || null,
            };
          }}
          onComplete={completeMembershipPurchase}
          onError={(message) => setErrorMsg(message)}
          onBack={() => {
            setMembershipPaymentSession(null);
            setErrorMsg("");
          }}
          errorMsg={errorMsg}
        />
      );
    }

    return (
      <MembershipPurchaseView
        memberships={availableMemberships}
        purchasingMembershipId={purchasingMembershipId}
        onPurchase={startMembershipPurchase}
        onBack={
          canPurchaseHireSession(lock)
            ? () => {
              setMembershipPaymentSession(null);
              setErrorMsg("");
              setAuthFlow(AuthFlow.ACCESS_PURCHASE_CHOICE);
            }
            : undefined
        }
        errorMsg={errorMsg}
        appleLink={appStoreLink}
        googleLink={googlePlayStoreLink}
      />
    );
  }

  if (authFlow === AuthFlow.HIRE_SELECT) {
    return (
      <StartHireView
        itemID={itemID}
        siteID={siteID}
        businessID={businessId}
        item={lock}
        site={site}
        phoneNumber={phoneNumber}
        name={name}
        stripePublishableKey={stripePublishableKey}
        mode={hireMode}
        existingSessionId={sessionId}
        existingSessionLength={sessionLength}
        onSessionReady={handleSessionReady}
        onCancel={() => setAuthFlow(AuthFlow.INPUT_DETAILS)}
      />
    );
  }

  if (authFlow === AuthFlow.LOCK_SESSION_HIRE) {
    return (
      <LockSessionHireView
        itemID={itemID}
        siteID={siteID}
        businessID={businessId}
        item={lock}
        site={site}
        weekdays={sessionOpeningHours.weekdays}
        specificDates={sessionOpeningHours.specificDates}
        sessionPurchaseAvailability={sessionPurchaseAvailability}
        getLiveSessionPurchaseAvailability={() =>
          getSessionPurchaseAvailability({
            weekdays: sessionOpeningHours.weekdays,
            specificDates: sessionOpeningHours.specificDates,
            siteTimezone: site?.timezone,
          })
        }
        phoneNumber={phoneNumber}
        name={name}
        stripePublishableKey={stripePublishableKey}
        onPaymentSuccess={handleLockSessionPaymentSuccess}
        onCancel={() => {
          setErrorMsg("");

          if (
            isMembershipOnly &&
            canPurchaseHireSession(lock) &&
            availableMemberships.length > 0
          ) {
            setAuthFlow(AuthFlow.ACCESS_PURCHASE_CHOICE);
            return;
          }

          setAuthFlow(AuthFlow.INPUT_DETAILS);
        }}
      />
    );
  }

  if (authFlow === AuthFlow.UNLOCKED) {
    return (
      <>
        <UnlockedView
          isAjaxItem={lock?.is_ajax_item}
          isHireStarted={isHireStarted}
          onStartHire={onStartHire}
          startingHire={startingHire}
          onUnlockAgain={onUnlockAgain}
          unlocking={unlocking}
          remainingTime={remainingTime}
          hireMaxLength={lock?.hire_max_length}
          sessionPurchasedTime={sessionLength}
          errorMsg={errorMsg}
          setAuthFlow={setAuthFlow}
          AuthFlowRef={AuthFlow}
          showConfetti={showConfetti}
          setHireMode={setHireMode}
          supportPhone={supportPhone}
          onReportDamage={() => setShowReportDamage(true)}
          hasReportDamage={hasReportDamage}
        />

        {showReportDamage && (
          <ReportDamageFlow
            opened={showReportDamage}
            siteID={siteID}
            itemID={itemID}
            businessID={businessId}
            sessionId={sessionId}
            phone={phoneNumber}
            onClose={() => setShowReportDamage(false)}
          />
        )}
      </>
    );
  }

  if (authFlow === AuthFlow.INPUT_DETAILS) {
    return (
      <InputDetailsView
        site={site}
        lock={lock}
        isCareZenBusiness={isCareZenBusiness}
        isBanned={isBanned}
        logoAnimation={logoAnimation}
        isLogoAnimationLoading={isLogoAnimationLoading}
        name={name}
        setName={setName}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        errorMsg={errorMsg}
        supportPhone={supportPhone}
        onSubmit={handleUnlockClick}
      />
    );
  }

  return (
    <NoLockView
      message={
        errorMsg || (invalidLock ? "This item is unavailable." : undefined)
      }
    />
  );
}
